/**
 * TutorialPlayerPage — learner-facing tutorial runtime.
 *
 * Used to consume curriculum tutorials seeded by
 * core/database/scenario_os/seed_defensive_tutorials.py (and any future
 * coach-authored tutorials that are published).
 *
 * Routes:
 *   /tutorials/:domain                  → list tutorials in a domain (optionally filtered to a track)
 *   /tutorials/:domain/play/:tutorialId → play a single tutorial through its segments
 *
 * Segment renderers supported:
 *   • explanation — script_text + optional video/audio (auto-detects
 *                   YouTube / Vimeo and embeds via iframe; plain MP4/WebM
 *                   URLs use the native <video> element)
 *   • question    — inline `questions` array (multiple choice with one correct
 *                   answer per question), client-side scoring + feedback
 *   • summary     — script_text only (treated as a wrap-up panel)
 *   • scenario    — deep-link to a SessionPage scene
 *   • slide_deck  — inline SlideDeckPlayer when deck_id is set; styled
 *                   "upload pending" placeholder until the coach uploads
 *                   the PPTX
 *   • reflection  — prompt + textarea + word counter; submission is captured
 *                   locally for now (server-side coach-review wiring is
 *                   deferred)
 *
 * Unknown segment types still render gracefully with a "Not yet supported"
 * placeholder so we never crash on a future segment shape.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { tutorialAPI } from '../services/api';
import SlideDeckPlayer from '../components/slide_deck/SlideDeckPlayer';
import CuratedResourceRenderer from '../components/scenes/CuratedResourceRenderer';
import PromptCanvasRenderer from '../components/scenes/PromptCanvasRenderer';
import NativeVideoPlayer from '../components/NativeVideoPlayer';
import TranscriptPanel from '../components/scenes/TranscriptPanel';
import ChatWidget from '../components/ChatWidget';
import { useDockablePanel } from '../../../shared/components/DockablePanel';
import NoteEditor from '../components/notes/NoteEditor';
import NoteButton from '../components/notes/NoteButton';
import NotesDockPanel from '../components/notes/NotesDockPanel';
import useTextSelection from '../hooks/useTextSelection';
import HighlightPopover from '../components/notes/HighlightPopover';
import HighlightRenderer from '../components/notes/HighlightRenderer';
import { emitLearnerEvent } from '../services/learnerEvents';
import BookmarkButton from '../components/notes/BookmarkButton';
import SubmissionFeedback from '../components/rubrics/SubmissionFeedback';
import CodeLabPlayer from '../components/scenes/CodeLabPlayer';
import PuzzlePlayer from '../components/scenes/PuzzlePlayer';
import { announce } from '../components/a11y/LiveRegion';
import DiscussionTab from '../components/discussions/DiscussionTab';
import CapstonePlayer from '../components/capstone/CapstonePlayer';
import AttachmentPanel from '../components/scenes/AttachmentPanel';
import CapstoneScene from '../components/scenes/CapstoneScene';
import CodeLabScene from '../components/scenes/CodeLabScene';
import MarginNotePanel from '../components/annotations/MarginNotePanel';
import ReadingPanel from '../components/scenes/ReadingPanel';
import { useSceneReviewGate } from '../components/overlays/SceneReviewGate';

const QuizCheckpointOverlay = React.lazy(() =>
  import('../components/VideoQuizOverlay').then((m) => ({ default: m.QuizCheckpointOverlay || m.default }))
);

const URL_PATTERN = /(https?:\/\/[^\s)<>]+)/g;

function SimpleMarkdownBlock({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <div style={{ fontSize: 15, lineHeight: 1.75, color: '#1f2937' }}>
      {lines.map((line, i) => {
        if (line.startsWith('### '))
          return <h4 key={i} style={{ margin: '18px 0 8px', fontSize: 16, fontWeight: 700, color: '#111827' }}>{line.slice(4)}</h4>;
        if (line.startsWith('## '))
          return <h3 key={i} style={{ margin: '22px 0 10px', fontSize: 18, fontWeight: 700, color: '#111827' }}>{line.slice(3)}</h3>;
        if (line.startsWith('# '))
          return <h2 key={i} style={{ margin: '24px 0 12px', fontSize: 20, fontWeight: 700, color: '#111827' }}>{line.slice(2)}</h2>;
        if (/^\d+\.\s\*\*/.test(line)) {
          const match = line.match(/^(\d+\.\s)\*\*(.+?)\*\*\s*[—–-]\s*(.*)/);
          if (match) return (
            <p key={i} style={{ margin: '6px 0', paddingLeft: 8 }}>
              <strong>{match[1]}{match[2]}</strong> — {match[3]}
            </p>
          );
        }
        if (line.startsWith('- ') || line.startsWith('* '))
          return <li key={i} style={{ marginLeft: 24, marginBottom: 4 }}>{renderInline(line.slice(2))}</li>;
        if (line.startsWith('> '))
          return (
            <blockquote key={i} style={{
              borderLeft: '3px solid #3b82f6', paddingLeft: 14, margin: '10px 0',
              color: '#334155', fontStyle: 'italic',
            }}>
              {renderInline(line.slice(2))}
            </blockquote>
          );
        if (line.trim() === '') return <div key={i} style={{ height: 8 }} />;
        return <p key={i} style={{ margin: '6px 0' }}>{renderInline(line)}</p>;
      })}
    </div>
  );
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : p
  );
}

function LinkifiedText({ text }) {
  if (!text) return null;
  const parts = text.split(URL_PATTERN);
  return parts.map((part, i) => {
    const isUrl = /^https?:\/\//.test(part);
    return isUrl ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer"
         style={{ color: '#2563eb', textDecoration: 'underline', wordBreak: 'break-all' }}>
        {part}
      </a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    );
  });
}

const SEGMENT_LABELS = {
  curated_resource: 'ADDITIONAL RESOURCES',
  prompt_canvas: 'PROMPT CANVAS',
  slide_deck: 'SLIDE DECK',
  ai_lab: 'AI LAB',
  code_lab: 'CODE LAB',
  reflection: 'REFLECTION',
  capstone: 'CAPSTONE PROJECT',
  project: 'PROJECT',
  question: 'QUIZ',
  quiz: 'QUIZ',
  explanation: 'LESSON',
  summary: 'SUMMARY',
  demonstration: 'DEMO',
  hook: 'INTRO',
  scenario: 'SCENARIO',
  puzzle: 'PUZZLE',
  content_panel: 'READING',
};

// ─── Auto-save helper — fire-and-forget, never blocks the UI ─────────────────
function autoSaveResource(segment) {
  if (!['curated_resource', 'prompt_canvas'].includes(segment?.segment_type)) return;
  const token = localStorage.getItem('scenario_os_token');
  if (!token) return;
  const steps = Array.isArray(segment.steps)
    ? segment.steps
    : (Array.isArray(segment.scene_data?.steps) ? segment.scene_data.steps : undefined);
  fetch('/scenario-os/curriculum/resources', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      domain: segment.domain || segment.scene_data?.domain || 'general',
      resource_url: segment.resource_url || segment.scene_data?.resource_url || (segment.segment_type === 'prompt_canvas' ? `app://canvas/${segment.segment_id || segment.scene_id || 'unknown'}` : ''),
      title: segment.title || 'Untitled Resource',
      source_name: segment.source_name || segment.scene_data?.source_name || '',
      module_id: segment.module_id || segment.scene_data?.module_id || null,
      difficulty: segment.difficulty || segment.scene_data?.difficulty || null,
      resource_type: segment.segment_type,
      topic_summary: segment.topic_summary || segment.scene_data?.topic_summary || '',
      key_insight: segment.key_insight || segment.scene_data?.key_insight || '',
      ...(steps ? { steps } : {}),
    }),
  }).catch(() => {}); // fire-and-forget, never block the UI
}

// ──────────────────────────────────────────────────────────────────────────
// Top-level: list view + player view (decided by route param)
// ──────────────────────────────────────────────────────────────────────────

export default function TutorialPlayerPage() {
  const { domain, tutorialId } = useParams();
  if (tutorialId) {
    return <TutorialPlayer key={tutorialId} domain={domain} tutorialId={tutorialId} />;
  }
  return <TutorialList domain={domain} />;
}


// ──────────────────────────────────────────────────────────────────────────
// List view
// ──────────────────────────────────────────────────────────────────────────

function TutorialList({ domain }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const trackId = params.get('track');

  const [tracks, setTracks] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      tutorialAPI.listTracks(domain).then((r) => r.data?.tracks || []).catch(() => []),
      tutorialAPI.list({ domain, ...(trackId ? { track_id: trackId } : {}) })
        .then((r) => r.data?.tutorials || [])
        .catch((e) => {
          setError(e?.response?.data?.detail || e?.message || 'Failed to load tutorials.');
          return [];
        }),
    ])
      .then(([tr, tut]) => { setTracks(tr); setTutorials(tut); })
      .finally(() => setLoading(false));
  }, [domain, trackId]);

  const grouped = useMemo(() => {
    if (trackId) {
      return [{
        track_id: trackId,
        display_name: tracks.find((t) => t.track_id === trackId)?.display_name || trackId,
        tutorials,
      }];
    }
    const byTrack = new Map();
    for (const t of tutorials) {
      const tid = t?.metadata?.curriculum_track_id || 'untracked';
      if (!byTrack.has(tid)) byTrack.set(tid, []);
      byTrack.get(tid).push(t);
    }
    const out = [];
    for (const tr of tracks) {
      out.push({
        track_id: tr.track_id,
        display_name: tr.display_name,
        description: tr.description,
        tutorials: byTrack.get(tr.track_id) || [],
      });
    }
    if (byTrack.has('untracked')) {
      out.push({
        track_id: 'untracked',
        display_name: 'Other tutorials',
        tutorials: byTrack.get('untracked'),
      });
    }
    return out;
  }, [tracks, tutorials, trackId]);

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <button style={backBtnStyle} onClick={() => navigate(-1)}>← Back</button>
        <h1 style={{ margin: 0, fontSize: 24 }}>Lessons — {domain}</h1>
      </header>

      {loading && <div style={mutedStyle}>Loading tutorials…</div>}
      {error && <div style={errorStyle}>{error}</div>}
      {!loading && tutorials.length === 0 && !error && (
        <div style={mutedStyle}>No published tutorials yet for this domain.</div>
      )}

      {grouped.map((group) => (
        <section key={group.track_id} style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 18, marginBottom: 4, color: '#1f2937' }}>
            {group.display_name}
          </h2>
          {group.description && (
            <p style={{ color: '#6b7280', marginTop: 0, marginBottom: 12, fontSize: 14 }}>
              {group.description}
            </p>
          )}
          <div style={cardGridStyle}>
            {group.tutorials.map((t) => (
              <button
                key={t.tutorial_id}
                style={cardStyle}
                onClick={() => navigate(`/tutorials/${domain}/play/${t.tutorial_id}`)}
              >
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>{t.title}</div>
                <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 8, lineHeight: 1.4 }}>
                  {t.description}
                </div>
                <div style={metaRowStyle}>
                  <span>{(t.segments || []).length} segments</span>
                  {t.estimated_duration_s && (
                    <span>~{Math.round(t.estimated_duration_s / 60)} min</span>
                  )}
                  <span style={difficultyBadge(t.difficulty)}>{t.difficulty || 'beginner'}</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Player view — segment-by-segment with quiz scoring
// ──────────────────────────────────────────────────────────────────────────

function TutorialPlayer({ domain, tutorialId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const isPreview = params.get('preview') === 'true' || params.get('from') === 'coach';
  const showTestModePicker = location.state?.test_mode_picker === true;
  const [testMode, setTestMode] = useState(null); // null = picker shown, {timed,showAnswers} = test started
  const [tutorial, setTutorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Deep-link: ?segment=N (0-based) from surface::OPEN / DomainPage Continue.
  // ?review=true → mastered module Review (restore answers, no gates, last scene).
  // ?replay=true → Start over from step 0 with gates on.
  const isReplay = params.get('replay') === 'true';
  const isReview = params.get('review') === 'true' || location.state?.review === true;
  const initialSeg = (() => {
    const raw = params.get('segment');
    if (raw != null && raw !== '') {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    if (isReplay) return 0;
    // Review without explicit segment: placeholder 0; clamped to last once tutorial loads.
    if (isReview) return 0;
    try {
      const keys = Object.keys(localStorage).filter((k) =>
        k.endsWith(`:${tutorialId}`) && k.startsWith('teachiq:tutorial_progress:'),
      );
      for (const k of keys) {
        const data = JSON.parse(localStorage.getItem(k) || '{}');
        const n = parseInt(data?.segmentIndex, 10);
        if (Number.isFinite(n) && n > 0) return n;
      }
    } catch { /* ignore */ }
    return 0;
  })();
  const [segIdx, setSegIdx] = useState(initialSeg);
  const warmLocal = (prefix) => {
    try {
      const keys = Object.keys(localStorage).filter((k) =>
        k.endsWith(`:${tutorialId}`) && k.startsWith(prefix),
      );
      for (const k of keys) {
        const data = JSON.parse(localStorage.getItem(k) || '{}');
        if (data && typeof data === 'object' && Object.keys(data).length) return data;
      }
    } catch { /* ignore */ }
    return {};
  };
  const [answers, setAnswers] = useState(() => warmLocal('teachiq:tutorial_answers:'));
  // Free-text submissions (reflection / ai_lab textarea) keyed by segment_id
  const [reflections, setReflections] = useState(() => warmLocal('teachiq:tutorial_reflections:'));
  // Structured drafts for ai_lab fields, prompt_canvas edits, etc.
  const [segmentState, setSegmentState] = useState(() => warmLocal('teachiq:tutorial_segment_state:'));
  const [progressHydrated, setProgressHydrated] = useState(false);
  // retryCount tracks how many "Try Again" retries a learner has used on each
  // assessment segment so we can enforce max_assessment_attempts.
  const [retryCount, setRetryCount] = useState({}); // { segmentId: number }
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completionToast, setCompletionToast] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(null);
  // Live codelab state lifted from CodeLabPlayer so ChatWidget has full context
  const [codelabChatContext, setCodelabChatContext] = useState(null);

  const {
    mode: chatMode, setMode: setChatMode,
    width: chatWidth, setWidth: setChatWidth,
    docked: chatDocked, bodyPadding: chatBodyPadding,
  } = useDockablePanel('teachiq_chat');

  // ── Notes & Bookmarks state ──
  const [noteEditorOpen, setNoteEditorOpen] = useState(false);
  const [notesDockOpen, setNotesDockOpen] = useState(false);
  const [showAnnotations, setShowAnnotations] = useState(false);
  // noteContextAnchor: { type: 'timestamp'|'slide', value: number } | null
  // Updated by child segments (video time, slide index) so the dock panel
  // pre-fills the correct context when the learner opens it.
  const [noteContextAnchor, setNoteContextAnchor] = useState(null);
  const [segmentNotes, setSegmentNotes] = useState({});
  const [bookmarkedSegments, setBookmarkedSegments] = useState(new Set());
  // notes-4: track which segments have already shown the "you have notes" toast
  const toastedSegsRef = React.useRef(new Set());
  // highlight: ref for the content area used by text-selection capture
  const contentAreaRef = React.useRef(null);
  const [notesToast, setNotesToast] = useState(null);

  const notesToken = localStorage.getItem('scenario_os_token');

  const fetchSegmentNotes = useCallback(async () => {
    if (!notesToken || !tutorialId) return;
    try {
      const res = await fetch(`/scenario-os/curriculum/notes?tutorial_id=${encodeURIComponent(tutorialId)}&limit=200`, {
        headers: { Authorization: `Bearer ${notesToken}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const bySegment = {};
      const bookmarks = new Set();
      for (const n of (data.notes || [])) {
        const sid = n.scene_id || n.segment_id;
        if (!bySegment[sid]) bySegment[sid] = [];
        bySegment[sid].push(n);
        if (n.note_type === 'bookmark') bookmarks.add(sid);
      }
      setSegmentNotes(bySegment);
      setBookmarkedSegments(bookmarks);
    } catch { /* non-critical */ }
  }, [notesToken, tutorialId]);

  useEffect(() => { fetchSegmentNotes(); }, [fetchSegmentNotes]);

  const handleSaveNote = async (payload) => {
    if (!notesToken) throw new Error('Not authenticated');
    const res = await fetch('/scenario-os/curriculum/notes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${notesToken}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.detail || `Save failed (${res.status})`);
    }
    fetchSegmentNotes();
  };

  const handleDeleteNote = async (noteId) => {
    if (!notesToken || !noteId) return;
    await fetch(`/scenario-os/curriculum/notes/${noteId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${notesToken}` },
    });
    fetchSegmentNotes();
  };

  const handleToggleBookmark = async (payload) => {
    if (!notesToken) return;
    const sid = payload.scene_id;
    if (bookmarkedSegments.has(sid)) {
      const existing = (segmentNotes[sid] || []).find((n) => n.note_type === 'bookmark');
      if (existing) {
        await fetch(`/scenario-os/curriculum/notes/${existing.note_id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${notesToken}` },
        });
      }
    } else {
      await fetch('/scenario-os/curriculum/notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${notesToken}` },
        body: JSON.stringify(payload),
      });
    }
    fetchSegmentNotes();
  };

  useEffect(() => {
    setLoading(true);
    setProgressHydrated(false);
    const token = localStorage.getItem('scenario_os_token');
    const loadProgress = async () => {
      // Server-side answers / left-off (preferred), then localStorage fallback.
      try {
        if (token) {
          const res = await fetch(
            `/scenario-os/curriculum/tutorial-progress/${encodeURIComponent(tutorialId)}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (res.ok) {
            const data = await res.json();
            if (data.answers && Object.keys(data.answers).length) {
              setAnswers((prev) => ({ ...data.answers, ...prev }));
            }
            if (data.reflections && Object.keys(data.reflections).length) {
              setReflections((prev) => ({ ...data.reflections, ...prev }));
            }
            if (data.segment_state && Object.keys(data.segment_state).length) {
              setSegmentState((prev) => ({ ...data.segment_state, ...prev }));
            }
            return data;
          }
        }
      } catch { /* non-critical */ }
      return null;
    };

    Promise.all([tutorialAPI.get(tutorialId), loadProgress()])
      .then(([r, saved]) => {
        const tut = r.data;
        // If the tutorial is a thin wrapper around a single timed_test_session scene
        // (e.g. LSAT PrepTest stubs), redirect directly to the test session player
        // so the learner gets the mode picker instead of a blank tutorial view.
        const segs = tut?.segments || [];
        if (segs.length === 1 && segs[0].segment_type === 'timed_test_session' && segs[0].scene_id) {
          const dom = tut.domain_id || tut.domain || domain;
          navigate(`/tests/${dom}/session/${segs[0].scene_id}`, { replace: true });
          return;
        }
        setTutorial(tut);
        const n = (tut?.segments || []).length;
        if (n > 0) {
          setSegIdx((i) => {
            // Review with no ?segment= → open the last scene in the module.
            if (isReview && (params.get('segment') == null || params.get('segment') === '')) {
              return n - 1;
            }
            // Explicit ?segment=N always wins (deep-link / Continue / Eva / suite 60),
            // including when combined with ?replay=true (Start over + jump to a scene).
            const rawSeg = params.get('segment');
            if (rawSeg != null && rawSeg !== '') {
              const parsed = parseInt(rawSeg, 10);
              if (Number.isFinite(parsed) && parsed >= 0) {
                return Math.min(parsed, n - 1);
              }
            }
            // Continue: prefer server left-off when URL has no segment.
            if (!isReplay && !isReview && saved?.segment_index > 0) {
              return Math.min(saved.segment_index, n - 1);
            }
            // Start Lesson (?replay=true, no segment) → step 0 with gates on.
            if (isReplay) return 0;
            return Math.min(Math.max(0, i), n - 1);
          });
        }
      })
      .catch((e) => setError(e?.response?.data?.detail || e?.message || 'Failed to load tutorial.'))
      .finally(() => {
        setLoading(false);
        setProgressHydrated(true);
      });
  }, [tutorialId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save resource segments when the active segment changes
  useEffect(() => {
    if (!tutorial) return;
    const segments = tutorial.segments || [];
    const currentSegment = segments[segIdx];
    if (currentSegment) autoSaveResource(currentSegment);
  }, [tutorial, segIdx]);

  // Announce segment transitions for screen readers
  useEffect(() => {
    if (!tutorial) return;
    const segments = tutorial.segments || [];
    const currentSegment = segments[segIdx];
    if (currentSegment) {
      announce(`Segment ${segIdx + 1} of ${segments.length}: ${currentSegment.title || currentSegment.segment_type}`);
    }
  }, [tutorial, segIdx]);

  // track-2: emit scene_viewed when segment loads; re-emit with duration_ms on leave
  // Dependencies intentionally limited to segIdx so the closure captures stable values.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!tutorial) return;
    const segments = tutorial.segments || [];
    const currentSegment = segments[segIdx];
    if (!currentSegment) return;
    const capturedSceneId = currentSegment.segment_id || currentSegment.scene_id;
    const capturedDomain = tutorial.domain_id || domain;
    const loadTime = Date.now();

    emitLearnerEvent({
      event_type: 'scene_viewed',
      scene_id: capturedSceneId,
      domain: capturedDomain,
      tutorial_id: tutorialId,
    });

    return () => {
      emitLearnerEvent({
        event_type: 'scene_viewed',
        scene_id: capturedSceneId,
        domain: capturedDomain,
        tutorial_id: tutorialId,
        duration_ms: Date.now() - loadTime,
      });
    };
  // Re-run only when the segment index changes (deliberate — captures scene at point of transition)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segIdx, !!tutorial]);

  // notes-4: show a one-time "you have N notes" toast when navigating to a scene with notes
  useEffect(() => {
    if (!tutorial) return;
    const segments = tutorial.segments || [];
    const currentSegment = segments[segIdx];
    if (!currentSegment) return;
    const sceneId = currentSegment.segment_id || currentSegment.scene_id;
    if (!sceneId || toastedSegsRef.current.has(sceneId)) return;
    const notes = (segmentNotes[sceneId] || []).filter((n) => n.note_type === 'note');
    if (notes.length > 0) {
      toastedSegsRef.current.add(sceneId);
      setNotesToast(notes.length);
      const tid = setTimeout(() => setNotesToast(null), 3500);
      return () => clearTimeout(tid);
    }
  }, [segIdx, segmentNotes, tutorial]);

  // Timer countdown for timed mode
  useEffect(() => {
    if (!testMode?.timed || timerSeconds === null || timerSeconds <= 0) return;
    const id = setInterval(() => setTimerSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [testMode, timerSeconds]);

  // Persist left-off + answers (local + server) for Continue / Review.
  // Must run before any early returns — React error #310 otherwise.
  useEffect(() => {
    if (!tutorialId || !tutorial || !progressHydrated) return;
    if (isReplay && segIdx === 0 && Object.keys(answers).length === 0) return;
    const learnerId = (() => {
      try {
        const tok = localStorage.getItem('scenario_os_token') || '';
        if (!tok || tok.split('.').length < 2) return 'anon';
        const payload = JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return payload.learner_id || payload.sub || 'anon';
      } catch {
        return 'anon';
      }
    })();
    try {
      localStorage.setItem(`teachiq:tutorial_progress:${learnerId}:${tutorialId}`, JSON.stringify({
        segmentIndex: segIdx,
        updatedAt: Date.now(),
        tutorialId,
      }));
      localStorage.setItem(`teachiq:tutorial_answers:${learnerId}:${tutorialId}`, JSON.stringify(answers));
      localStorage.setItem(`teachiq:tutorial_reflections:${learnerId}:${tutorialId}`, JSON.stringify(reflections));
      localStorage.setItem(`teachiq:tutorial_segment_state:${learnerId}:${tutorialId}`, JSON.stringify(segmentState));
    } catch { /* ignore quota / private mode */ }

    // Debounced server upsert (fire-and-forget)
    const token = localStorage.getItem('scenario_os_token');
    if (!token || isPreview) return undefined;
    const tid = setTimeout(() => {
      fetch('/scenario-os/curriculum/tutorial-progress', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          tutorial_id: tutorialId,
          segment_index: segIdx,
          answers,
          reflections,
          segment_state: segmentState,
          completed: isReview,
        }),
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(tid);
  }, [segIdx, tutorialId, tutorial, answers, reflections, segmentState, isReplay, isReview, isPreview, progressHydrated]);

  // notes-3: compute notesPerSlide + highlight notes — must be declared BEFORE
  // any early returns so React always calls the same number of hooks per render.
  const _segments = tutorial?.segments || [];
  const _seg = _segments[segIdx];
  const currentSceneId = _seg?.segment_id || _seg?.scene_id;
  const notesPerSlide = React.useMemo(() => {
    if (!currentSceneId) return {};
    const sceneNoteList = segmentNotes[currentSceneId] || [];
    const out = {};
    for (const n of sceneNoteList) {
      if (n.slide_index != null) {
        out[n.slide_index] = (out[n.slide_index] || 0) + 1;
      }
    }
    return out;
  }, [currentSceneId, segmentNotes]);

  // highlight: text-selection hook + per-scene highlight notes
  const { selection: textSelection, clearSelection } = useTextSelection(contentAreaRef);
  const highlightNotes = React.useMemo(() => {
    if (!currentSceneId) return [];
    return (segmentNotes[currentSceneId] || []).filter((n) => n.note_type === 'highlight');
  }, [currentSceneId, segmentNotes]);

  if (loading) return <div style={pageStyle}><div style={mutedStyle}>Loading tutorial…</div></div>;
  if (error)   return <div style={pageStyle}><div style={errorStyle}>{error}</div></div>;
  if (!tutorial) return null;

  // Test mode picker for practice tests
  if (showTestModePicker && !testMode) {
    const meta = tutorial.metadata || {};
    const sections = meta.sections || 4;
    const minsPerSection = meta.minutes_per_section || 35;
    const intermission = meta.intermission_minutes || 10;
    const timeLimitMin = meta.time_limit_minutes || (sections * minsPerSection);
    const totalQuestions = (tutorial.segments || []).reduce((n, s) => n + (s.questions?.length || 0), 0);
    const totalWithBreak = timeLimitMin + intermission;
    const hours = Math.floor(totalWithBreak / 60);
    const mins = totalWithBreak % 60;
    const durationLabel = hours > 0 ? `${hours}h ${mins}m` : `${totalWithBreak} min`;

    return (
      <div style={pageStyle}>
        <header style={headerStyle}>
          <button style={backBtnStyle} onClick={() => navigate(-1)}>← Back</button>
          <h1 style={{ margin: 0, fontSize: 24 }}>{tutorial.title || 'Practice Test'}</h1>
        </header>
        <div style={{ maxWidth: 560, margin: '60px auto', padding: '0 24px' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#1e3a5f' }}>
              Choose Your Test Mode
            </h2>
            <p style={{ margin: '0 0 16px', color: '#666', fontSize: 14, lineHeight: 1.5 }}>
              {tutorial.description || `This test has ${totalQuestions} questions.`}
            </p>
            <div style={{
              display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20,
              padding: '12px 16px', background: '#f0f4f8', borderRadius: 8, fontSize: 13, color: '#475569',
            }}>
              <span><strong>{sections}</strong> sections</span>
              <span><strong>{minsPerSection} min</strong> per section</span>
              <span><strong>{intermission} min</strong> intermission</span>
              <span><strong>{totalQuestions}</strong> questions</span>
              <span>Total: <strong>{durationLabel}</strong></span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                onClick={() => { setTestMode({ timed: true, showAnswers: false }); setTimerSeconds(timeLimitMin * 60); }}
                style={{
                  padding: '14px 20px', borderRadius: 8, border: '2px solid #1e3a5f', background: '#1e3a5f', color: '#fff',
                  fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                }}
              >
                Timed Test — {sections} sections × {minsPerSection} min ({timeLimitMin} min)
                <div style={{ fontSize: 12, fontWeight: 400, opacity: 0.85, marginTop: 4 }}>
                  Simulate real LSAT conditions with a {timeLimitMin}-minute countdown and a {intermission}-minute intermission
                </div>
              </button>
              <button
                onClick={() => setTestMode({ timed: false, showAnswers: false })}
                style={{
                  padding: '14px 20px', borderRadius: 8, border: '2px solid #1e3a5f', background: '#fff', color: '#1e3a5f',
                  fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                }}
              >
                Untimed Test
                <div style={{ fontSize: 12, fontWeight: 400, color: '#666', marginTop: 4 }}>
                  Take the full test at your own pace without time pressure
                </div>
              </button>
              <button
                onClick={() => setTestMode({ timed: false, showAnswers: true })}
                style={{
                  padding: '14px 20px', borderRadius: 8, border: '2px solid #27ae60', background: '#fff', color: '#27ae60',
                  fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                }}
              >
                Practice Mode — Show Answers As You Go
                <div style={{ fontSize: 12, fontWeight: 400, color: '#666', marginTop: 4 }}>
                  See correct answers after each question for study purposes
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const segments = tutorial.segments || [];
  const seg = segments[segIdx];
  const isLast = segIdx === segments.length - 1;
  const segmentDomain = tutorial.domain_id || domain;

  // currentSceneId / notesPerSlide / textSelection / highlightNotes are declared
  // above (before the loading/error early returns) so hook call count is stable.

  const setAnswer = (segId, qId, choiceId) =>
    setAnswers((prev) => ({
      ...prev,
      [segId]: { ...(prev[segId] || {}), [qId]: choiceId },
    }));

  const setReflection = (segId, text) =>
    setReflections((prev) => ({ ...prev, [segId]: text }));

  const patchSegmentState = (segId, patch) =>
    setSegmentState((prev) => ({
      ...prev,
      [segId]: { ...(prev[segId] || {}), ...patch, updatedAt: Date.now() },
    }));

  const bookmarkProgress = (segmentIndex) => {
    try {
      const tok = localStorage.getItem('scenario_os_token') || '';
      let learnerId = 'anon';
      if (tok && tok.split('.').length >= 2) {
        try {
          const payload = JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
          learnerId = payload.learner_id || payload.sub || 'anon';
        } catch { /* keep anon */ }
      }
      localStorage.setItem(`teachiq:tutorial_progress:${learnerId}:${tutorialId}`, JSON.stringify({
        segmentIndex,
        updatedAt: Date.now(),
        tutorialId,
      }));
    } catch { /* ignore quota / private mode */ }
  };

  const goPrev = () => {
    try {
      window.dispatchEvent(new Event('teachiq:unload-curated-embeds'));
    } catch { /* ignore */ }
    const next = Math.max(0, segIdx - 1);
    bookmarkProgress(next);
    window.setTimeout(() => setSegIdx(next), 40);
  };
  const goNext = () => {
    // Blank YouTube/Vimeo iframes *before* React unmounts them — otherwise the
    // main thread can freeze 30s+ advancing past curated resources (Hova W3).
    try {
      window.dispatchEvent(new Event('teachiq:unload-curated-embeds'));
    } catch { /* ignore */ }
    const next = Math.min(segments.length - 1, segIdx + 1);
    // Eager local bookmark so a hung render still resumes near the destination.
    bookmarkProgress(next);
    window.setTimeout(() => setSegIdx(next), 40);
  };

  const completeTutorial = async () => {
    if (completing) return;
    // Review of an already-mastered module — don't re-submit for XP/mastery.
    if (isReview) {
      goBack();
      return;
    }
    setCompleting(true);

    // Clear left-off bookmark — module finished
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('teachiq:tutorial_progress:') && k.endsWith(`:${tutorialId}`))
        .forEach((k) => localStorage.removeItem(k));
    } catch { /* ignore */ }

    if (isPreview) {
      setCompletionToast({ xp: 0, mastered: false, preview: true });
      setTimeout(() => { goBack(); }, 1500);
      return;
    }

    const token = localStorage.getItem('scenario_os_token');
    const moduleId = tutorial.module_id || tutorial.metadata?.curriculum_module_id || tutorial.metadata?.module_id || '';
    const trackId = tutorial.track_id || tutorial.metadata?.curriculum_track_id || tutorial.metadata?.track_id || '';
    let qCorrect = 0, qTotal = 0;
    const QUIZ_SEGMENT_TYPES = new Set(['question', 'quiz', 'interactive_quiz', 'assessment']);

    // Use ONLY the final quiz segment (the assessment) to determine mastery.
    // Mid-tutorial checkpoint quizzes are practice — they must not dilute the
    // score from the real assessment the learner just completed.
    // Also, only count questions the learner actually answered in THIS session
    // so that stale data from earlier attempts cannot drag accuracy down.
    const quizSegments = segments.filter((s) => QUIZ_SEGMENT_TYPES.has(s.segment_type));
    const assessmentSeg = quizSegments.length > 0 ? quizSegments[quizSegments.length - 1] : null;

    if (assessmentSeg) {
      const segAnswers = answers[assessmentSeg.segment_id] || {};
      (assessmentSeg.questions || []).forEach((q) => {
        const chosen = segAnswers[q.question_id];
        if (chosen) {
          qTotal++;
          const choice = (q.choices || []).find((c) => c.choice_id === chosen);
          if (choice?.correct || choice?.is_correct) qCorrect++;
        }
      });
    }

    // Guard: don't award completion on empty tutorials with no segments
    if (segments.length === 0) {
      setCompleting(false);
      goBack();
      return;
    }

    try {
      const res = await fetch(`/scenario-os/curriculum/${encodeURIComponent(segmentDomain)}/tutorials/${encodeURIComponent(tutorialId)}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          module_id: moduleId,
          track_id: trackId,
          questions_correct: qCorrect,
          questions_total: qTotal,
          answers,
          reflections,
          segment_state: segmentState,
          segment_index: segIdx,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const xp = data.xp_earned || 0;
        if (xp > 0 || data.is_mastered || qTotal > 0) {
          setCompletionToast({
            xp,
            mastered: data.is_mastered,
            nextModuleId: data.next_module_id || null,
          });
          // Auto-navigate after 5 s — toast has an explicit "Back to Modules" button
          // for learners who want to act immediately.
          setTimeout(() => { goBack(); }, 5000);
        } else {
          // No quiz segments — just navigate back
          goBack();
        }
        return;
      }
    } catch { /* fall through to goBack */ }
    setCompleting(false);
    goBack();
  };

  // ``goBack`` returns the learner to wherever they launched the lesson
  // from (Content Library, Curriculum module, etc.) by stepping back in
  // history. Falls back to the home page if there's no history (deep
  // link straight into the lesson). Critical: we deliberately do NOT
  // navigate to ``/tutorials/:domain`` because that page can land users
  // in a dead-end when filters return zero results.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  // ── Quiz gating ────────────────────────────────────────────────────────────
  // Shared set for all quiz segment types (mirrors completeTutorial logic).
  const QUIZ_SEG_TYPES = new Set(['question', 'quiz', 'interactive_quiz', 'assessment']);

  const isQuizSeg     = QUIZ_SEG_TYPES.has(seg?.segment_type);
  const segQuestions  = seg?.questions || [];
  const segAnswers    = answers[seg?.segment_id] || {};

  // All questions must be answered before Next/Done enables (both quiz_role types).
  // Review mode skips gates — answers are restored from prior completion.
  const allAnswered = isReview || !(isQuizSeg && segQuestions.length > 0)
    ? true
    : segQuestions.every((q) => segAnswers[q.question_id]);

  // Assessment segments additionally require a passing score.
  const isAssessmentSeg = seg?.quiz_role === 'assessment';
  const minPassScore    = seg?.min_pass_score ?? 0.7;
  const maxAttempts     = seg?.max_assessment_attempts ?? 100;
  const currentRetries  = retryCount[seg?.segment_id] || 0;

  // Compute live score once all questions are answered on an assessment segment.
  let segScore = 0;
  if (isAssessmentSeg && (isReview || allAnswered) && segQuestions.length > 0) {
    const answered = segQuestions.filter((q) => segAnswers[q.question_id]);
    const correct = answered.filter((q) => {
      const chosen = segAnswers[q.question_id];
      const choice = (q.choices || []).find((c) => c.choice_id === chosen);
      return choice?.correct || choice?.is_correct;
    }).length;
    if (answered.length > 0) segScore = correct / answered.length;
    else if (isReview) segScore = 1; // prior mastery — allow free navigation
  }

  const assessmentPassed = isReview || !isAssessmentSeg
    ? true
    : (allAnswered && segScore >= minPassScore);

  // AI Lab segments require at minimum a non-empty submission (enforced at
  // 30 words, matching the UI "aim for at least 30 words" guidance).
  // The min_submission_words field on the segment overrides the default.
  const isAiLabSeg = seg?.segment_type === 'ai_lab';
  const aiLabMinWords = seg?.min_submission_words ?? 30;
  const aiLabValue = reflections[seg?.segment_id] || '';
  const aiLabWordCount = aiLabValue.trim() ? aiLabValue.trim().split(/\s+/).length : 0;
  const aiLabGatePassed = isReview || !isAiLabSeg || aiLabWordCount >= aiLabMinWords;

  // Reflection segments: enforce min_words when set (Hova W3 — 120-word gate).
  const isReflectionSeg = seg?.segment_type === 'reflection';
  const reflectionMinWords = Number(
    seg?.min_words
    ?? seg?.domain_metadata?.min_words
    ?? seg?.metadata?.min_words
    ?? 0,
  ) || 0;
  const reflectionValue = reflections[seg?.segment_id] || '';
  const reflectionWordCount = reflectionValue.trim()
    ? reflectionValue.trim().split(/\s+/).length
    : 0;
  const reflectionGatePassed = isReview || !isReflectionSeg || reflectionMinWords <= 0
    || reflectionWordCount >= reflectionMinWords;

  // Next / Done is enabled when all questions are answered (checkpoint),
  // the assessment passes (assessment gate), AI Lab meets word minimum,
  // and Reflection meets min_words when configured.
  // Review always allows free navigation.
  const nextEnabled = isReview || (
    allAnswered
    && (!isAssessmentSeg || assessmentPassed)
    && aiLabGatePassed
    && reflectionGatePassed
  );

  // "Try Again" clears answers for this segment and increments the retry counter.
  const handleRetry = (segId) => {
    setAnswers((prev) => ({ ...prev, [segId]: {} }));
    setRetryCount((prev) => ({ ...prev, [segId]: (prev[segId] || 0) + 1 }));
  };
  // ── end quiz gating ────────────────────────────────────────────────────────

  const effectivePageStyle = {
    ...pageStyle,
    ...(chatDocked && showChat ? {
      maxWidth: 'none',
      paddingRight: chatBodyPadding + 24,
      transition: 'padding-right 0.2s',
    } : {}),
  };

  return (
    <div style={effectivePageStyle} role="main" id="main-content">
      <header style={headerStyle}>
        <button style={backBtnStyle} onClick={goBack}>← Back</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>{tutorial.title}</h1>
          <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>{tutorial.description}</div>
        </div>
        <button
          style={{ ...backBtnStyle, color: '#3b82f6', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}
          onClick={() => setCanvasOpen(true)}
          title="Open your saved canvases"
        >
          📋 My Canvas
        </button>
        <button
          style={{ ...backBtnStyle, color: '#7c3aed', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}
          onClick={() => navigate('/my-notes')}
          title="View all your saved notes and highlights"
        >
          📝 My Notes
        </button>
        <button
          style={{ ...backBtnStyle, color: '#6b7280' }}
          onClick={() => navigate('/')}
          title="Return to home"
        >
          ✕ Close
        </button>
      </header>

      {isPreview && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 100,
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          color: '#fff', padding: '8px 16px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          fontWeight: 600, fontSize: '0.875rem',
          borderRadius: 8, marginBottom: 12,
        }}>
          <span>PREVIEW MODE — Changes will not affect learners</span>
          <button onClick={() => window.history.back()} style={{
            background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)',
            color: '#fff', padding: '4px 12px', borderRadius: 6, cursor: 'pointer',
            fontWeight: 600, fontSize: '0.8rem',
          }}>
            Exit Preview
          </button>
        </div>
      )}

      {canvasOpen && <CanvasDrawer domain={segmentDomain || domain} onClose={() => setCanvasOpen(false)} />}

      <AttributionBanner segment={seg} />
      <ProgressBar current={segIdx + 1} total={segments.length} />

      {/* Timer bar for timed practice tests */}
      {testMode?.timed && timerSeconds !== null && (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8,
          padding: '8px 16px', marginBottom: 8,
          background: timerSeconds <= 300 ? '#fef2f2' : '#f0f9ff',
          borderRadius: 8, fontSize: 14, fontWeight: 600,
          color: timerSeconds <= 300 ? '#dc2626' : '#1e3a5f',
        }}>
          <span>{timerSeconds <= 0 ? 'Time is up!' : `${timerSeconds >= 3600 ? Math.floor(timerSeconds / 3600) + ':' : ''}${String(Math.floor((timerSeconds % 3600) / 60)).padStart(timerSeconds >= 3600 ? 2 : 1, '0')}:${String(timerSeconds % 60).padStart(2, '0')} remaining`}</span>
        </div>
      )}

      {seg && (
        <section ref={contentAreaRef} style={segmentCardStyle} aria-label={seg.title || `Segment ${segIdx + 1}`} data-segment-type={seg.segment_type || 'content'} data-segment-locked={seg.is_locked ? 'true' : 'false'}>
          <HighlightRenderer containerRef={contentAreaRef} highlights={highlightNotes} />
          <div style={segmentHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={segmentBadgeStyle}>{SEGMENT_LABELS[seg.segment_type] || (seg.segment_type || 'segment').toUpperCase()}</span>
                {seg.access_tier === 'free' && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                    padding: '2px 8px', borderRadius: 4, background: '#ecfdf5', color: '#059669',
                  }}>FREE</span>
                )}
                {seg.is_locked && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                    padding: '2px 8px', borderRadius: 4, background: '#fef3c7', color: '#d97706',
                  }}>LOCKED</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <NoteButton
                  onClick={() => setNotesDockOpen(true)}
                  hasNotes={!!(segmentNotes[seg.segment_id || seg.scene_id] || []).filter((n) => n.note_type === 'note').length}
                  contextAnchor={noteContextAnchor}
                />
                <BookmarkButton
                  isBookmarked={bookmarkedSegments.has(seg.segment_id || seg.scene_id)}
                  onToggle={handleToggleBookmark}
                  sceneId={seg.segment_id || seg.scene_id}
                  tutorialId={tutorialId}
                  domain={segmentDomain}
                />
                <button
                  onClick={() => setShowAnnotations((v) => !v)}
                  title="Toggle community annotations for this scene"
                  style={{
                    padding: '5px 10px',
                    borderRadius: 6,
                    border: `1px solid ${showAnnotations ? '#a855f7' : '#d1d5db'}`,
                    background: showAnnotations ? '#f3e8ff' : '#fff',
                    color: showAnnotations ? '#7c3aed' : '#6b7280',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    transition: 'all 0.15s',
                  }}
                >
                  💬 Annotations
                </button>
              </div>
            </div>
            {seg.segment_type !== 'puzzle' && (
              <h2 style={{ margin: '6px 0 12px', fontSize: 18, color: '#1f2937' }}>{seg.title}</h2>
            )}
          </div>
          {/* Pre-assessment banner — shown when first arriving at a gating quiz */}
          {isAssessmentSeg && !allAnswered && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              background: '#eff6ff', border: '1px solid #bfdbfe',
              borderRadius: 10, padding: '12px 16px', marginBottom: 16,
            }}>
              <span style={{ fontSize: 20 }}>📋</span>
              <div>
                <div style={{ fontWeight: 700, color: '#1e40af', fontSize: '0.9rem' }}>
                  Final Assessment — answer all {segQuestions.length} question{segQuestions.length !== 1 ? 's' : ''}
                </div>
                <div style={{ color: '#3b82f6', fontSize: '0.82rem', marginTop: 2 }}>
                  You need <strong>{Math.round(minPassScore * 100)}%</strong> to complete this module and unlock the next one.
                  {currentRetries > 0 && (
                    <span style={{ marginLeft: 8, color: '#6b7280' }}>
                      Attempt {currentRetries + 1}
                      {maxAttempts < 9999 ? ` of ${maxAttempts}` : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {seg.is_locked ? (
            <LockedSegmentOverlay segmentType={seg.segment_type} onUpgrade={() => {}} />
          ) : (
            <>
              <SegmentRenderer
                segment={seg}
                segments={segments}
                domain={segmentDomain}
                tutorialId={tutorialId}
                answers={answers[seg.segment_id] || {}}
                onAnswer={(qId, choiceId) => setAnswer(seg.segment_id, qId, choiceId)}
                reflections={reflections}
                onReflection={setReflection}
                segmentDraft={segmentState[seg?.segment_id] || {}}
                onSegmentDraft={(patch) => patchSegmentState(seg.segment_id, patch)}
                onComplete={isLast ? completeTutorial : goNext}
                showAnswersImmediately={isReview || testMode?.showAnswers === true}
                reviewMode={isReview}
                onContextAnchorChange={setNoteContextAnchor}
                notesPerSlide={notesPerSlide}
                onCodeLabContextChange={setCodelabChatContext}
              />

              {/* Post-submit result panel — shown only for assessment segments
                  once all questions are answered */}
              {isAssessmentSeg && allAnswered && (
                <div style={{
                  marginTop: 20, borderRadius: 10, padding: '16px 20px',
                  background: assessmentPassed ? '#f0fdf4' : '#fef2f2',
                  border: `1px solid ${assessmentPassed ? '#86efac' : '#fca5a5'}`,
                }}>
                  <div style={{
                    fontWeight: 700, fontSize: '1rem',
                    color: assessmentPassed ? '#15803d' : '#dc2626',
                    marginBottom: assessmentPassed ? 12 : 6,
                  }}>
                    {assessmentPassed
                      ? `✅ You scored ${Math.round(segScore * 100)}% — requirement met!`
                      : `❌ You scored ${Math.round(segScore * 100)}% — you need ${Math.round(minPassScore * 100)}% to continue.`}
                  </div>

                  {/* When the assessment is passed on the last segment, show a
                      prominent "Continue →" button here that calls completeTutorial()
                      directly. This prevents the UX trap where learners see the
                      "requirement met!" text and navigate away (Back/Close) before
                      clicking the navigation "Done" button — which is the only way
                      mastery gets recorded. Both this button and "Done" are equivalent;
                      whichever is clicked first records the completion. */}
                  {assessmentPassed && isLast && (
                    <button
                      onClick={completeTutorial}
                      disabled={completing}
                      style={{
                        display: 'block', width: '100%',
                        background: completing ? '#86efac' : '#059669',
                        color: '#fff', border: 'none',
                        padding: '12px 24px', borderRadius: 8,
                        cursor: completing ? 'not-allowed' : 'pointer',
                        fontWeight: 700, fontSize: '1rem',
                        marginBottom: 8, transition: 'background 0.2s',
                      }}
                    >
                      {completing ? 'Recording your progress…' : 'Continue to Next Module →'}
                    </button>
                  )}

                  {!assessmentPassed && (
                    currentRetries < maxAttempts ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                        <button
                          onClick={() => handleRetry(seg.segment_id)}
                          style={{
                            background: '#ef4444', color: '#fff', border: 'none',
                            padding: '8px 18px', borderRadius: 8, cursor: 'pointer',
                            fontWeight: 700, fontSize: '0.875rem',
                          }}
                        >
                          Try Again
                        </button>
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                          Attempt {currentRetries + 1}
                          {maxAttempts < 9999 ? ` of ${maxAttempts}` : ''}
                        </span>
                      </div>
                    ) : (
                      <div style={{
                        marginTop: 8, padding: '10px 14px', borderRadius: 8,
                        background: '#fef3c7', color: '#92400e',
                        fontSize: '0.875rem', fontWeight: 600,
                      }}>
                        You've reached the retry limit. Exit now and return when you're ready to try again.
                      </div>
                    )
                  )}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* highlight: floating popover on text selection */}
      {textSelection && (
        <HighlightPopover
          rect={textSelection.rect}
          selectedText={textSelection.text}
          sceneId={currentSceneId}
          tutorialId={tutorialId}
          domain={segmentDomain}
          onSave={async (noteData) => {
            await handleSaveNote({
              ...noteData,
              scene_id: noteData.scene_id || currentSceneId,
              tutorial_id: noteData.tutorial_id || tutorialId,
              domain: noteData.domain || segmentDomain,
            });
            clearSelection();
            // Brief confirmation — highlights were previously silent on success
            setNotesToast('highlight');
            setTimeout(() => setNotesToast(null), 2500);
          }}
          onDismiss={clearSelection}
        />
      )}

      {/* notes-5: Dockable notes panel — replaces the standalone NoteEditor */}
      <NotesDockPanel
        isOpen={notesDockOpen}
        onClose={() => setNotesDockOpen(false)}
        sceneId={seg?.segment_id || seg?.scene_id}
        tutorialId={tutorialId}
        domain={segmentDomain}
        notes={(segmentNotes[seg?.segment_id || seg?.scene_id] || []).filter((n) => n.note_type === 'note')}
        onSaveNote={handleSaveNote}
        onDeleteNote={handleDeleteNote}
        contextAnchor={noteContextAnchor}
      />

      {/* P16.7: Social annotation panel */}
      {showAnnotations && (
        <MarginNotePanel
          sceneId={currentSceneId}
          domain={segmentDomain}
          isOpen={showAnnotations}
          onClose={() => setShowAnnotations(false)}
          currentTimestampSec={noteContextAnchor?.type === 'timestamp' ? noteContextAnchor.value : null}
          currentSlideIndex={noteContextAnchor?.type === 'slide' ? noteContextAnchor.value : null}
        />
      )}

      {/* Legacy NoteEditor kept for programmatic use elsewhere (existingNote editing) */}
      <NoteEditor
        isOpen={noteEditorOpen}
        onClose={() => setNoteEditorOpen(false)}
        onSave={handleSaveNote}
        sceneId={seg?.segment_id || seg?.scene_id}
        tutorialId={tutorialId}
        domain={segmentDomain}
      />

      {/* notes-4: toast for scene-revisit count OR highlight-saved confirmation */}
      {notesToast != null && (
        <div style={{
          position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)',
          background: '#1e293b', color: '#e2e8f0',
          padding: '10px 20px', borderRadius: 8,
          fontSize: 14, fontWeight: 500, zIndex: 9998,
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', gap: 8,
          pointerEvents: 'none',
        }}>
          <span>📝</span>
          {notesToast === 'highlight'
            ? 'Highlight saved'
            : `You have ${notesToast} note${notesToast === 1 ? '' : 's'} for this scene`}
        </div>
      )}

      <DiscussionTab
        sceneId={seg?.segment_id || seg?.scene_id}
        tutorialId={tutorialId}
        domain={segmentDomain || domain}
      />

      {completionToast && (
        <div style={{
          position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
          background: completionToast.preview ? '#d97706' : completionToast.mastered ? '#059669' : '#3b82f6',
          color: '#fff', padding: '14px 28px', borderRadius: 12, fontSize: 15,
          fontWeight: 600, boxShadow: '0 4px 20px rgba(0,0,0,0.2)', zIndex: 9999,
          display: 'flex', alignItems: 'center', gap: 10, flexDirection: 'column',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {completionToast.preview ? (
              <>
                <span style={{ fontSize: 22 }}>👁</span>
                Preview complete — no XP or mastery recorded
              </>
            ) : (
              <>
                <span style={{ fontSize: 22 }}>{completionToast.mastered ? '🏆' : '✓'}</span>
                Tutorial complete! +{completionToast.xp} XP
                {completionToast.mastered && <span> · Module mastered!</span>}
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            <button
              onClick={goBack}
              style={{
                background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)',
                padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >
              ← Back to Modules
            </button>
            {completionToast.nextModuleId && (
              <button
                onClick={() => {
                  const d = segmentDomain || domain || 'lsat';
                  navigate(`/domain/${d}`);
                }}
                style={{
                  background: '#fff', color: '#059669', border: 'none',
                  padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                }}
              >
                Continue to Next Module →
              </button>
            )}
          </div>
        </div>
      )}

      {seg?.segment_type !== 'slide_deck' && (
        <nav style={navBarStyle} id="navigation" aria-label="Segment navigation">
          <button style={navBtnStyle(false)} onClick={goPrev} disabled={segIdx === 0}>← Previous</button>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#6b7280', fontSize: 13 }} aria-current="step">
              Segment {segIdx + 1} of {segments.length}
            </div>
            {/* Hint text when Next is blocked by unanswered quiz questions */}
            {isQuizSeg && !allAnswered && (
              <div style={{ color: '#d97706', fontSize: 11, fontWeight: 600, marginTop: 3 }}>
                Answer all questions to continue
              </div>
            )}
            {/* Hint when all answered but assessment score too low */}
            {isAssessmentSeg && allAnswered && !assessmentPassed && (
              <div style={{ color: '#dc2626', fontSize: 11, fontWeight: 600, marginTop: 3 }}>
                Score {Math.round(minPassScore * 100)}% or higher to continue
              </div>
            )}
            {/* Hint when AI Lab submission doesn't meet minimum word count */}
            {isAiLabSeg && !aiLabGatePassed && (
              <div style={{ color: '#d97706', fontSize: 11, fontWeight: 600, marginTop: 3 }}>
                Submit your AI Lab response ({aiLabWordCount}/{aiLabMinWords} words) to continue
              </div>
            )}
            {isReflectionSeg && !reflectionGatePassed && (
              <div style={{ color: '#d97706', fontSize: 11, fontWeight: 600, marginTop: 3 }}>
                Write at least {reflectionMinWords} words to continue ({reflectionWordCount}/{reflectionMinWords})
              </div>
            )}
          </div>
          {isLast ? (
            <button
              style={{
                ...navBtnStyle(true),
                opacity: nextEnabled ? 1 : 0.45,
                cursor: nextEnabled ? 'pointer' : 'not-allowed',
              }}
              onClick={nextEnabled ? completeTutorial : undefined}
              disabled={!nextEnabled}
              title={!nextEnabled ? 'Answer all questions to continue' : undefined}
            >
              {isReview ? 'Back to Modules' : 'Done'}
            </button>
          ) : (
            <button
              style={{
                ...navBtnStyle(true),
                opacity: nextEnabled ? 1 : 0.45,
                cursor: nextEnabled ? 'pointer' : 'not-allowed',
              }}
              onClick={nextEnabled ? goNext : undefined}
              disabled={!nextEnabled}
              title={!nextEnabled ? 'Answer all questions to continue' : undefined}
            >
              Next →
            </button>
          )}
        </nav>
      )}

      {/* Raise-hand button — opens chat with domain coach */}
      <button
        onClick={() => setShowChat((v) => !v)}
        aria-label={showChat ? 'Close coach chat' : 'Ask your coach'}
        title={showChat ? 'Close coach chat' : 'Ask your coach'}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: showChat ? '#ef4444' : '#3b82f6',
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          fontSize: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10002,
          transition: 'background 0.2s',
        }}
      >
        {showChat ? '✕' : '✋'}
      </button>

      {showChat && (
        <ChatWidget
          domain={segmentDomain}
          defaultOpen
          onClose={() => setShowChat(false)}
          lessonContext={{
            tutorialId: tutorialId,
            tutorialTitle: tutorial?.title,
            segmentIndex: segIdx,
            segmentTitle: seg?.title,
            segmentType: seg?.segment_type,
            // Codelab live state — populated by CodeLabPlayer via onContextChange
            ...(codelabChatContext || {}),
          }}
          panelMode={chatMode}
          onPanelModeChange={setChatMode}
          panelWidth={chatWidth}
          onPanelResize={setChatWidth}
        />
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Locked segment overlay — shown when a segment requires a paid tier
// ──────────────────────────────────────────────────────────────────────────

const SEGMENT_PREVIEWS = {
  explanation: 'Video lesson',
  question: 'Practice quiz',
  quiz: 'Practice quiz',
  scenario: 'Interactive scenario',
  slide_deck: 'Slide presentation',
  reflection: 'Guided reflection',
  ai_lab: 'AI Lab exercise',
  curated_resource: 'Curated resource',
  prompt_canvas: 'Prompt Canvas workshop',
  code_lab: 'Code Lab exercise',
  capstone: 'Capstone project',
};

function LockedSegmentOverlay({ segmentType, onUpgrade }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '48px 24px', textAlign: 'center', minHeight: 200,
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: 8, border: '1px dashed #cbd5e1',
    }}>
      <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.6 }}>
        🔒
      </div>
      <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#1e293b' }}>
        Premium Content
      </h3>
      <p style={{ margin: '0 0 6px', fontSize: 14, color: '#64748b', maxWidth: 400 }}>
        This {SEGMENT_PREVIEWS[segmentType] || 'content'} is available with a paid subscription.
      </p>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: '#94a3b8' }}>
        Upgrade to unlock all lessons, quizzes, labs, and assessments.
      </p>
      <button
        onClick={onUpgrade}
        style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          color: '#fff', border: 'none', borderRadius: 8,
          padding: '10px 28px', fontSize: 14, fontWeight: 700,
          cursor: 'pointer', boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
        }}
      >
        Upgrade to Premium
      </button>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Attribution banner — only on the *current* segment when that segment's
// media is third-party (YouTube / Khan / external). Coach-uploaded / TeachIQ-
// hosted media (GCS, portal uploads, empty "Original" license) must NOT
// trigger the YouTube disclaimer.
// ──────────────────────────────────────────────────────────────────────────

/** Segment types that can carry third-party video / licensed media. */
const ATTRIBUTION_MEDIA_TYPES = new Set([
  'video',
  'explanation',
  'demonstration',
  'hook',
  'summary',
  'slide_deck',
  'content_panel',
]);

/** Licenses that mean coach/platform original — never show 3rd-party banner. */
const ORIGINAL_LICENSES = new Set(['', 'original', 'proprietary', 'teachiq', 'coach_owned', 'none']);

function _segmentMediaUrl(segment) {
  if (!segment) return '';
  return (
    segment.video_url
    || segment.media_url
    || segment.scene_data?.video_url
    || segment.scene_data?.media_url
    || segment.domain_metadata?.video_url
    || ''
  ).trim();
}

function _isYoutubeUrl(url) {
  return /(?:youtube\.com|youtu\.be)\b/i.test(url || '');
}

function _isKhanUrl(url) {
  return /khanacademy\.org/i.test(url || '');
}

/** Coach uploads / TeachIQ storage — not third-party YouTube embeds. */
function _isTeachIqHostedUrl(url) {
  if (!url) return false;
  if (url.startsWith('/') || url.startsWith('blob:') || url.startsWith('data:')) return true;
  return (
    /storage\.googleapis\.com/i.test(url)
    || /\.googleapis\.com\//i.test(url)
    || /flowgenixlabs\.com/i.test(url)
    || /flowgenix/i.test(url)
    || /\/coach\/(?:video|slide-deck|audio|media)\//i.test(url)
    || /\/scenario-os\/(?:media|video|uploads)\//i.test(url)
    || /\/api\/v\d+\/.*\/(?:media|video|upload)/i.test(url)
  );
}

/**
 * True only when this segment actually embeds YouTube (URL or declared
 * youtube_embed that is not a TeachIQ-hosted upload).
 */
function _isYoutubeMedia(segment) {
  const url = _segmentMediaUrl(segment);
  if (_isTeachIqHostedUrl(url)) return false;
  if (_isYoutubeUrl(url)) return true;
  const license = String(segment.content_license || '').toLowerCase();
  if (ORIGINAL_LICENSES.has(license)) return false;
  // License tag alone is not enough if URL is missing/hosted; require YouTube
  // URL, or youtube_embed with no conflicting hosted URL.
  if (license === 'youtube_embed') {
    // Declared YouTube — trust only when URL is missing or is actually YouTube.
    // Hosted/upload URLs already returned false above.
    if (!url) return true;
    return _isYoutubeUrl(url);
  }
  if (segment.provider === 'youtube' && !_isTeachIqHostedUrl(url)) {
    return !url || _isYoutubeUrl(url);
  }
  return false;
}

function _isKhanMedia(segment) {
  const url = _segmentMediaUrl(segment);
  if (_isTeachIqHostedUrl(url)) return false;
  if (_isKhanUrl(url)) return true;
  if (/khan/i.test(segment.content_attribution || '')) return true;
  return String(segment.content_license || '').toLowerCase() === 'cc_by_nc_sa'
    && !_isTeachIqHostedUrl(url);
}

function AttributionBanner({ segment }) {
  if (!segment) return null;
  const type = segment.segment_type || segment.content_type || '';
  // Labs, quizzes, assessments, reflections, etc. never show this banner.
  if (type && !ATTRIBUTION_MEDIA_TYPES.has(type)) return null;

  const license = String(segment.content_license || '').toLowerCase();
  if (ORIGINAL_LICENSES.has(license) && !_isYoutubeMedia(segment) && !_isKhanMedia(segment)) {
    return null;
  }

  const hasYoutube = _isYoutubeMedia(segment);
  const hasKhan = _isKhanMedia(segment);
  const hasExternal = license === 'external_link' && !_isTeachIqHostedUrl(_segmentMediaUrl(segment));
  if (!hasYoutube && !hasKhan && !hasExternal) return null;

  // Prefer segment-level attribution when present (e.g. "IBM Technology — Maya Murad")
  const custom = segment.content_attribution ? [segment.content_attribution] : [];
  const uniqueCustom = [...new Set(custom)];

  let message;
  if (uniqueCustom.length && (hasYoutube || hasKhan || hasExternal)) {
    message = (
      <>
        Some materials in this lesson are third-party content:
        {' '}{uniqueCustom.join('; ')}.
        {hasYoutube ? ' Embedded via YouTube for educational use.' : ''}
      </>
    );
  } else if (hasKhan && hasYoutube) {
    message = (
      <>
        Some materials in this lesson are third-party video embeds and/or
        Creative Commons content. Khan Academy materials are available free at{' '}
        <a href="https://www.khanacademy.org" target="_blank" rel="noopener noreferrer"
          style={{ color: '#1d4ed8', fontWeight: 600 }}>
          khanacademy.org
        </a>.
      </>
    );
  } else if (hasKhan) {
    message = (
      <>
        Some materials in this lesson are courtesy of Khan Academy under a
        Creative Commons license, available free at{' '}
        <a href="https://www.khanacademy.org" target="_blank" rel="noopener noreferrer"
          style={{ color: '#1d4ed8', fontWeight: 600 }}>
          khanacademy.org
        </a>.
      </>
    );
  } else if (hasYoutube) {
    message = (
      <>
        Some materials in this lesson are third-party YouTube embeds used for
        educational illustration. They are not TeachIQ original productions.
      </>
    );
  } else {
    message = (
      <>
        Some materials in this lesson are courtesy of their respective creators
        under external licenses.
      </>
    );
  }

  return (
    <div style={{
      padding: '8px 16px', marginBottom: 8, borderRadius: 6,
      background: '#eff6ff', border: '1px solid #bfdbfe',
      fontSize: 11, color: '#1e40af', lineHeight: 1.5,
    }}>
      {message}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Segment renderer — small dispatch table on segment_type
// ──────────────────────────────────────────────────────────────────────────

function SegmentRenderer({ segment, segments, domain, tutorialId, answers, onAnswer, reflections, onReflection, segmentDraft, onSegmentDraft, onComplete, showAnswersImmediately, reviewMode, onContextAnchorChange, notesPerSlide, onCodeLabContextChange }) {
  const type = segment.segment_type;

  // Gate onComplete for every segment type that supports a natural completion
  // event.  Self-rated types (flashcard, timed_test_session, etc.) are excluded
  // automatically by useSceneReviewGate based on the segment_type field.
  const { gatedComplete, ratingOverlay } = useSceneReviewGate({
    sceneOrSegment: segment,
    domain,
    onComplete,
    enabled: !['question', 'quiz', 'scenario'].includes(type),
  });
  const hasRubric = !!(
    segment.domain_metadata?.rubric
    || segment.scene_data?.domain_metadata?.rubric
  );
  const rubricSceneId = segment.scene_id || segment.segment_id;

  let content;
  if (type === 'explanation' || type === 'summary' || type === 'demonstration' || type === 'hook') {
    content = <ExplanationSegment segment={segment} onComplete={gatedComplete} onContextAnchorChange={onContextAnchorChange} />;
  } else if (type === 'question' || type === 'quiz') {
    content = (
      <QuestionSegment
        segment={segment}
        answers={answers}
        onAnswer={onAnswer}
        showAnswersImmediately={showAnswersImmediately}
        reviewMode={!!reviewMode}
      />
    );
  } else if (type === 'scenario' && segment.scenario_id) {
    content = <ScenarioSegment segment={segment} domain={domain} />;
  } else if (type === 'slide_deck') {
    content = <SlideDeckSegment segment={segment} onComplete={gatedComplete} notesPerSlide={notesPerSlide} onContextAnchorChange={onContextAnchorChange} />;
  } else if (type === 'content_panel') {
    content = <ContentPanelSegment segment={segment} onComplete={gatedComplete} />;
  } else if (type === 'reflection') {
    content = (
      <ReflectionSegment
        segment={segment}
        value={reflections[segment.segment_id] || ''}
        onChange={(v) => onReflection(segment.segment_id, v)}
      />
    );
  } else if (type === 'ai_lab') {
    const allSegments = segments || [];
    let priorReflection = '';
    const currentIdx = allSegments.indexOf(segment);
    for (let i = currentIdx - 1; i >= 0; i--) {
      const st = allSegments[i].segment_type;
      if (st === 'reflection' && reflections[allSegments[i].segment_id]) {
        priorReflection = reflections[allSegments[i].segment_id];
        break;
      }
    }
    content = (
      <AiLabSegment
        segment={segment}
        value={reflections[segment.segment_id] || ''}
        onChange={(v) => onReflection(segment.segment_id, v)}
        priorReflection={priorReflection}
        draft={segmentDraft}
        onDraftChange={onSegmentDraft}
      />
    );
  } else if (type === 'curated_resource') {
    content = <CuratedResourceRenderer segment={segment} onComplete={gatedComplete} />;
  } else if (type === 'prompt_canvas') {
    content = (
      <PromptCanvasRenderer
        segment={segment}
        onComplete={gatedComplete}
        draft={segmentDraft}
        onDraftChange={onSegmentDraft}
      />
    );
  } else if (type === 'capstone' || type === 'project') {
    content = <CapstonePlayer
      segment={segment}
      domain={domain}
      sceneType={type}
      onComplete={gatedComplete}
    />;
  } else if (type === 'code_lab') {
    // Always use CodeLabPlayer — it handles JupyterLite (ai_orchestrator labs),
    // local test execution (ai_beginner labs), hints, submission history, and
    // Markdown instructions.  CodeLabScene was a stub that read the wrong field
    // (instructions vs instructions_md) and never ran tests.
    content = (
      <CodeLabPlayer
        scene={segment}
        domain={domain}
        onContextChange={onCodeLabContextChange}
      />
    );
  } else if (type === 'puzzle') {
    const puzzleScene = {
      ...(segment.scene_data || {}),
      ...segment,
      scene_id: segment.scene_id || segment.scene_data?.scene_id || segment.segment_id,
      domain_metadata:
        segment.domain_metadata
        || segment.scene_data?.domain_metadata
        || {},
    };
    content = (
      <PuzzlePlayer
        scene={puzzleScene}
        domain={domain}
        tutorialId={tutorialId || segment.tutorial_id}
        segmentId={segment.segment_id}
        onComplete={gatedComplete}
      />
    );
  } else {
    content = (
      <div style={mutedStyle}>
        Segment type "<code>{type}</code>" is not yet supported by the learner runtime.
      </div>
    );
  }

  const showRubricWidget = hasRubric && ['reflection', 'ai_lab', 'prompt_canvas'].includes(type);

  const attachments = segment.domain_metadata?.attachments
    || segment.scene_data?.attachments
    || segment.attachments
    || [];

  return (
    <>
      {content}
      {showRubricWidget && (
        <RubricSubmissionWidget
          sceneId={rubricSceneId}
          submissionText={reflections[segment.segment_id] || ''}
        />
      )}
      {attachments.length > 0 && (
        <AttachmentPanel
          attachments={attachments}
          sceneId={segment.scene_id || segment.segment_id}
        />
      )}
      {ratingOverlay}
    </>
  );
}


function ScenarioSegment({ segment, domain }) {
  // ``scenario`` segments deep-link to a Scenario OS scene (e.g. a
  // defensive front quiz) inside a lesson. We use a same-tab navigation
  // rather than an iframe because:
  //   1) The platform CSP sets ``frame-ancestors 'none'``, which blocks
  //      same-origin iframe embedding entirely — every iframe rendered
  //      blank regardless of URL correctness.
  //   2) The SessionPage manages its own audio + scene state, which
  //      collides messily with the surrounding lesson player when
  //      embedded.
  // Same-tab navigation lets the learner play the scene with the full
  // session UI; the browser back button (or our "← Back" header)
  // returns them to the lesson where they left off.
  const navigate = useNavigate();
  const sceneId = segment.scenario_id;
  const dom = domain || 'football';
  const path = `/session/${encodeURIComponent(dom)}/${encodeURIComponent(sceneId)}`;
  return (
    <div>
      {segment.script_text && (
        <p style={{ fontSize: 15, lineHeight: 1.6, color: '#1f2937', whiteSpace: 'pre-wrap', marginBottom: 16 }}>
          {segment.script_text}
        </p>
      )}
      <div style={{
        background: 'linear-gradient(135deg, #0b1220 0%, #1e3a5f 100%)',
        borderRadius: 10,
        padding: 32,
        textAlign: 'center',
        color: '#fff',
      }}>
        <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.85 }}>🏈</div>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>
          {segment.title || 'Interactive Scene'}
        </div>
        <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>
          Spotlights each defender and quizzes you on the technique. Use Back to return to this lesson when done.
        </div>
        <button
          onClick={() => navigate(path)}
          style={{
            background: '#3b82f6',
            color: '#fff',
            border: 0,
            borderRadius: 6,
            padding: '10px 24px',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          ▶ Play Scene
        </button>
      </div>
    </div>
  );
}



function ExplanationSegment({ segment, onComplete, onContextAnchorChange }) {
  const videoSrc = segment.video_url || segment.scene_data?.video_url;
  const segmentId = segment.segment_id || segment.id;
  const sceneId = segment.scene_id || segment.scenario_id;
  const transcriptSegments = segment.domain_metadata?.transcript_segments
    || segment.scene_data?.transcript_segments
    || segment.transcript_segments;

  const [videoTime, setVideoTime] = React.useState(0);
  const seekRef = React.useRef(null);

  const handleSeek = React.useCallback((seconds) => {
    if (seekRef.current) {
      seekRef.current(seconds);
    }
  }, []);

  // notes-1: propagate current video time as context anchor so the NoteButton
  // in the parent header can pre-fill "📍 at M:SS" when opening the notes panel.
  const handleVideoTimeUpdate = React.useCallback((time) => {
    setVideoTime(time);
    if (onContextAnchorChange) {
      onContextAnchorChange({ type: 'timestamp', value: time });
    }
  }, [onContextAnchorChange]);

  return (
    <div>
      {videoSrc && (
        <NativeVideoPlayer
          src={videoSrc}
          segmentId={segmentId}
          sceneId={sceneId}
          onComplete={onComplete}
          title={segment.title}
          mcqMarkers={segment.mcq_markers || segment.scene_data?.mcq_markers}
          onTimeUpdateExternal={handleVideoTimeUpdate}
          seekRef={seekRef}
        />
      )}
      {segment.workspace_capture_url && (
        <img
          src={segment.workspace_capture_url}
          alt={segment.title}
          style={{ width: '100%', borderRadius: 8, marginBottom: 12 }}
        />
      )}
      {(() => {
        const bodyMd = segment.domain_metadata?.body_markdown
          || segment.scene_data?.domain_metadata?.body_markdown;
        if (bodyMd) return <SimpleMarkdownBlock text={bodyMd} />;
        if (segment.script_text) {
          return (
            <p style={{ fontSize: 15, lineHeight: 1.6, color: '#1f2937', whiteSpace: 'pre-wrap' }}>
              {segment.script_text}
            </p>
          );
        }
        return null;
      })()}
      {(segment.voice_audio_url || segment.script_text) && !videoSrc && (
        <div style={{ marginTop: 12 }}>
          <SlideNarrationPlayer
            audioUrl={segment.voice_audio_url}
            speakerNotes={segment.script_text}
          />
        </div>
      )}
      {transcriptSegments && transcriptSegments.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <TranscriptPanel
            segments={transcriptSegments}
            currentTime={videoTime}
            onSeek={handleSeek}
          />
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Slide-deck segment — inline SlideDeckPlayer when a deck has been
// uploaded; "upload pending" placeholder when the curriculum seed
// hasn't been backed by a real PPTX yet (the partner uploads the deck
// from the Coach Library).
// ─────────────────────────────────────────────────────────────────────
function SlideDeckSegment({ segment, onComplete, notesPerSlide, onContextAnchorChange }) {
  // Precedence:
  //   1. Uploaded PPTX/PDF deck (deck_id set)  → SlideDeckPlayer (pdfjs)
  //   2. Authored inline slides (segment.slides set) → InlineSlideDeck
  //   3. Neither → "upload pending" placeholder
  // This lets seeded curricula ship usable slides out of the box (no
  // coach upload required), while preserving the PPTX upload path as
  // the eventual production storage.
  if (segment.deck_id) {
    return (
      <div>
        {segment.script_text && (
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.6,
              color: '#1f2937',
              whiteSpace: 'pre-wrap',
              marginBottom: 12,
            }}
          >
            {segment.script_text}
          </p>
        )}
        <SlideDeckPlayer deckId={segment.deck_id} notesPerSlide={notesPerSlide} />
      </div>
    );
  }

  const inlineSlides = Array.isArray(segment.slides) ? segment.slides
    : Array.isArray(segment.domain_metadata?.slides) ? segment.domain_metadata.slides
    : Array.isArray(segment.scene_data?.slides) ? segment.scene_data.slides
    : [];
  if (inlineSlides.length > 0) {
    const mcqMarkers = segment.slide_mcq_markers || segment.scene_data?.slide_mcq_markers || [];
    return (
      <div>
        {segment.script_text && (
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.6,
              color: '#1f2937',
              whiteSpace: 'pre-wrap',
              marginBottom: 12,
            }}
          >
            {segment.script_text}
          </p>
        )}
        <InlineSlideDeck
          slides={inlineSlides}
          onComplete={onComplete}
          mcqMarkers={mcqMarkers}
          notesPerSlide={notesPerSlide}
          onContextAnchorChange={onContextAnchorChange}
        />
      </div>
    );
  }

  // No deck and no authored slides — show the upload-pending placeholder.
  return (
    <div>
      {segment.script_text && (
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.6,
            color: '#1f2937',
            whiteSpace: 'pre-wrap',
            marginBottom: 12,
          }}
        >
          {segment.script_text}
        </p>
      )}
      <div
        style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          color: '#e2e8f0',
          borderRadius: 10,
          padding: 32,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 40, marginBottom: 12 }}>🗂️</div>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>
          Slide deck upload pending
        </div>
        <div style={{ fontSize: 13, color: '#cbd5e1', maxWidth: 480, margin: '0 auto', lineHeight: 1.5 }}>
          The coach can either upload a PowerPoint deck from Coach
          Authoring → Slide Decks, or author slides inline by setting
          <code style={{ background: '#0f172a', padding: '1px 5px', borderRadius: 3, margin: '0 4px' }}>
            domain_metadata.slides
          </code>
          on the scene.
        </div>
        {segment.slide_count > 0 && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#94a3b8' }}>
            Expected slide count: {segment.slide_count}
          </div>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// InlineSlideDeck — lightweight presenter for authored slides.
//
// Each slide is `{ title, bullets: [...], body?, speaker_notes? }`.
// No PDF.js, no upload required — renders directly from the scene
// metadata so seeded curricula are playable from a fresh install.
//
// Keyboard: ← / → to navigate, S to toggle speaker notes.
// ─────────────────────────────────────────────────────────────────────
function SlideNarrationPlayer({ audioUrl, speakerNotes }) {
  const audioRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(false);
  const [usingSpeech, setUsingSpeech] = React.useState(false);
  const utterRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (audioRef.current) { try { audioRef.current.pause(); } catch (_) {} }
      if (typeof window !== 'undefined') window.speechSynthesis?.cancel();
    };
  }, []);

  React.useEffect(() => {
    if (audioRef.current) { try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch (_) {} }
    if (typeof window !== 'undefined') window.speechSynthesis?.cancel();
    setPlaying(false);
    setUsingSpeech(false);
  }, [audioUrl, speakerNotes]);

  const toggle = () => {
    if (playing) {
      if (usingSpeech) {
        window.speechSynthesis?.cancel();
      } else if (audioRef.current) {
        audioRef.current.pause();
      }
      setPlaying(false);
      return;
    }

    if (audioUrl) {
      const audio = audioRef.current;
      if (!audio) return;
      audio.currentTime = 0;
      audio.play().then(() => { setPlaying(true); setUsingSpeech(false); })
        .catch(() => _fallbackToSpeech());
    } else {
      _fallbackToSpeech();
    }
  };

  const _fallbackToSpeech = () => {
    if (!speakerNotes || typeof window === 'undefined' || !window.speechSynthesis) return;
    const utt = new SpeechSynthesisUtterance(speakerNotes);
    utt.rate = 0.95;
    utt.onend = () => setPlaying(false);
    utt.onerror = () => setPlaying(false);
    utterRef.current = utt;
    window.speechSynthesis.speak(utt);
    setPlaying(true);
    setUsingSpeech(true);
  };

  if (!audioUrl && !speakerNotes) return null;

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="auto"
          onEnded={() => setPlaying(false)}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
        />
      )}
      <button
        onClick={toggle}
        title={playing ? 'Pause narration' : 'Listen to narration'}
        style={{
          background: playing ? '#ef4444' : '#3b82f6',
          color: '#fff',
          border: 'none',
          borderRadius: 20,
          padding: '5px 12px',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          transition: 'background 0.15s',
        }}
      >
        {playing ? '\u23F8' : '\u25B6'} {playing ? 'Pause' : 'Listen'}
        {usingSpeech && <span style={{ fontSize: 10, opacity: 0.7 }}>(browser)</span>}
      </button>
    </div>
  );
}


function InlineSlideDeck({ slides, onComplete, mcqMarkers, notesPerSlide, onContextAnchorChange }) {
  const [idx, setIdx] = React.useState(0);
  const [showNotes, setShowNotes] = React.useState(false);
  const [activeQuiz, setActiveQuiz] = React.useState(null);
  const [answeredSlides, setAnsweredSlides] = React.useState(new Set());
  const total = slides.length;
  const slide = slides[idx] || {};

  // notes-2: propagate current slide index as context anchor
  React.useEffect(() => {
    if (onContextAnchorChange) {
      onContextAnchorChange({ type: 'slide', value: idx });
    }
  }, [idx, onContextAnchorChange]);

  React.useEffect(() => {
    if (!mcqMarkers?.length) return;
    const marker = mcqMarkers.find(
      (m) => m.slide_index === idx && !answeredSlides.has(m.marker_id)
    );
    if (marker?.question) {
      setActiveQuiz(marker);
    }
  }, [idx, mcqMarkers, answeredSlides]);

  React.useEffect(() => {
    const handler = (e) => {
      if (activeQuiz) return;
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(0, i - 1));
      else if (e.key === 'ArrowRight') setIdx((i) => Math.min(total - 1, i + 1));
      else if (e.key === 's' || e.key === 'S') setShowNotes((v) => !v);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [total, activeQuiz]);

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
      {/* Slide canvas */}
      <div
        style={{
          position: 'relative',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#f8fafc',
          padding: '32px 36px',
          minHeight: 360,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: slide.bullets || slide.body ? 'flex-start' : 'center',
        }}
      >
        <div
          style={{
            fontSize: 11,
            letterSpacing: 1.5,
            color: '#94a3b8',
            textTransform: 'uppercase',
            marginBottom: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>Slide {idx + 1} of {total}</span>
          {(slide.audio_url || slide.speaker_notes || slide.notes) && (
            <SlideNarrationPlayer audioUrl={slide.audio_url} speakerNotes={slide.speaker_notes || slide.notes} />
          )}
        </div>
        <h2 style={{ fontSize: 26, margin: '0 0 18px', lineHeight: 1.25 }}>{slide.title || `Slide ${idx + 1}`}</h2>
        {slide.image_url && (
          <div style={{ marginBottom: 16, borderRadius: 8, overflow: 'hidden', textAlign: 'center' }}>
            <img
              src={slide.image_url}
              alt={slide.title || 'Slide image'}
              style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain', borderRadius: 8, maxHeight: 320 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}
        {Array.isArray(slide.bullets || slide.bullet_points) && (slide.bullets || slide.bullet_points).length > 0 && (
          <ul style={{ fontSize: 16, lineHeight: 1.7, paddingLeft: 22, margin: 0 }}>
            {(slide.bullets || slide.bullet_points).map((b, i) => (
              <li key={i} style={{ marginBottom: 6 }}>{b}</li>
            ))}
          </ul>
        )}
        {(slide.body || slide.content) && (
          <p style={{ fontSize: 15, lineHeight: 1.7, marginTop: 12, color: '#cbd5e1', whiteSpace: 'pre-wrap' }}>
            {slide.body || slide.content}
          </p>
        )}

          {activeQuiz && (
          <React.Suspense fallback={null}>
            <QuizCheckpointOverlay
              question={activeQuiz.question}
              maxAttempts={activeQuiz.max_attempts || 2}
              onAnswer={() => {}}
              onDismiss={() => {
                setAnsweredSlides((prev) => new Set(prev).add(activeQuiz.marker_id));
                setActiveQuiz(null);
              }}
              continueLabel="Continue Slides"
              markerId={activeQuiz.marker_id}
            />
          </React.Suspense>
        )}
      </div>

      {/* Speaker notes */}
      {(slide.speaker_notes || slide.notes) && showNotes && (
        <div
          style={{
            background: '#fef3c7',
            color: '#78350f',
            padding: '12px 20px',
            fontSize: 13,
            lineHeight: 1.6,
            borderBottom: '1px solid #fde68a',
          }}
        >
          <strong style={{ display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
            Speaker notes
          </strong>
          <span style={{ whiteSpace: 'pre-wrap' }}>{slide.speaker_notes || slide.notes}</span>
        </div>
      )}

      {/* Controls bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          background: '#f8fafc',
        }}
      >
        <button
          onClick={() => !activeQuiz && setIdx((i) => Math.max(0, i - 1))}
          disabled={idx === 0 || !!activeQuiz}
          style={controlBtn(idx === 0 || !!activeQuiz)}
        >
          ← Prev
        </button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {(slide.speaker_notes || slide.notes) && (
            <button onClick={() => setShowNotes((v) => !v)} style={controlBtn(false)}>
              {showNotes ? 'Hide notes (S)' : 'Show notes (S)'}
            </button>
          )}
          <span style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
            {idx + 1} / {total}
            {/* notes-3: badge showing note count for the current slide */}
            {notesPerSlide && notesPerSlide[idx] > 0 && (
              <span style={{
                background: '#f59e0b',
                color: '#1e293b',
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 700,
                padding: '1px 6px',
                minWidth: 16,
                textAlign: 'center',
              }} title={`${notesPerSlide[idx]} note(s) on this slide`}>
                {notesPerSlide[idx]}
              </span>
            )}
          </span>
        </div>
        <button
          onClick={() => {
            if (activeQuiz) return;
            if (idx >= total - 1 && onComplete) onComplete();
            else setIdx((i) => Math.min(total - 1, i + 1));
          }}
          disabled={!!activeQuiz}
          style={controlBtn(!!activeQuiz)}
        >
          {idx >= total - 1 ? 'Continue →' : 'Next →'}
        </button>
      </div>
    </div>
  );
}

const controlBtn = (disabled) => ({
  background: disabled ? '#e2e8f0' : '#1e293b',
  color: disabled ? '#94a3b8' : '#f8fafc',
  border: 'none',
  padding: '6px 12px',
  borderRadius: 6,
  fontSize: 13,
  cursor: disabled ? 'default' : 'pointer',
  fontWeight: 500,
});


// ─────────────────────────────────────────────────────────────────────
// ContentPanelSegment — ov-5: renders body_markdown content and then
// shows QuizCheckpointOverlay for each content_mcq_marker in sequence.
// ─────────────────────────────────────────────────────────────────────
function ContentPanelSegment({ segment, onComplete }) {
  const markers = (
    segment.content_mcq_markers ||
    segment.domain_metadata?.content_mcq_markers ||
    segment.scene_data?.content_mcq_markers ||
    []
  ).filter((m) => m.question?.prompt && m.question?.choices?.length);

  const [answeredCount, setAnsweredCount] = React.useState(0);
  const activeMarker = answeredCount < markers.length ? markers[answeredCount] : null;

  const handleMarkerDismiss = React.useCallback(() => {
    const next = answeredCount + 1;
    setAnsweredCount(next);
    if (next >= markers.length && onComplete) onComplete();
  }, [answeredCount, markers.length, onComplete]);

  const meta = segment.domain_metadata || segment.scene_data?.domain_metadata || {};
  const body = meta.body_markdown
    || meta.content_markdown
    || meta.reading_body
    || meta.script_text
    || segment.script_text;

  return (
    <div style={{ position: 'relative' }}>
      {body && (
        <ReadingPanel
          text={body}
          eyebrow={meta.content_type === 'content_panel' ? 'Lesson reading' : 'Reading'}
        />
      )}
      {/* ov-5: MCQ checkpoints shown as an overlay on the content panel */}
      {activeMarker && (
        <React.Suspense fallback={null}>
          <QuizCheckpointOverlay
            question={activeMarker.question}
            maxAttempts={activeMarker.max_attempts || 2}
            onAnswer={() => {}}
            onDismiss={handleMarkerDismiss}
            continueLabel={answeredCount + 1 < markers.length ? 'Next Question' : 'Continue'}
            markerId={activeMarker.marker_id}
          />
        </React.Suspense>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Reflection segment — prompt + textarea + live word counter.
// Submissions are captured locally for now.  When coach-review wiring
// lands, this component will POST to /scenario-os/reflections.
// ─────────────────────────────────────────────────────────────────────
function ReflectionSegment({ segment, value, onChange }) {
  const minWords = Number(segment.min_words) || 0;
  const wordCount = (value || '').trim() ? value.trim().split(/\s+/).length : 0;
  const meetsMin = !minWords || wordCount >= minWords;
  const [showExample, setShowExample] = React.useState(false);

  const exampleResponse = segment.example_response || segment.domain_metadata?.example_response || segment.metadata?.example_response ||
    'Example: "In our customer onboarding process, a Research Agent could pull CRM data and past support tickets, while a Drafting Agent writes a personalized welcome sequence. A Review Agent checks tone and compliance before sending. The human approves the final batch. This saves ~45 minutes per new customer and reduces onboarding errors by catching compliance issues automatically."';

  return (
    <div>
      <div
        style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderRadius: 10,
          padding: 18,
          marginBottom: 14,
          color: '#78350f',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
          Reflection prompt
        </div>
        <div style={{ fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {segment.prompt || segment.script_text || 'Write a short reflection.'}
        </div>
      </div>
      <button
        onClick={() => setShowExample((v) => !v)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 600, color: '#3b82f6', padding: 0,
          marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5,
        }}
      >
        {showExample ? '▾ Hide' : '▸ See an'} example response
      </button>
      {showExample && (
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
          padding: 14, marginBottom: 14, fontSize: 13.5, lineHeight: 1.6,
          color: '#166534', fontStyle: 'italic',
        }}>
          {exampleResponse}
        </div>
      )}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Type your reflection here…"
        rows={8}
        style={{
          width: '100%',
          padding: 12,
          border: '1px solid #d1d5db',
          borderRadius: 8,
          fontSize: 14,
          fontFamily: 'inherit',
          lineHeight: 1.5,
          resize: 'vertical',
          boxSizing: 'border-box',
        }}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 8,
          fontSize: 12,
          color: meetsMin ? '#059669' : '#6b7280',
        }}
      >
        <span>
          {wordCount} word{wordCount === 1 ? '' : 's'}
          {minWords > 0 && ` (min ${minWords})`}
        </span>
        {minWords > 0 && meetsMin && <span>✓ Minimum met</span>}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// AI Lab segment — structured practice challenge with AI tool launcher.
//
// The learner fills in [bracketed] placeholders in the starter prompt,
// then clicks a button to open their preferred AI tool (ChatGPT / Claude /
// Gemini) in a new tab with the prompt pre-loaded via query parameter.
// After working through the challenge, they paste their result + annotation
// into the submission area for coach review.
//
// Phase 1: deep-link launcher only (no API key required).
// Phase 2 (future): embedded AI chat widget when tenant API key is set.
// ─────────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  { id: 'claude',  label: 'Claude',  icon: '🟠', color: '#d97706', url: (p) => `https://claude.ai/new?q=${encodeURIComponent(p)}` },
  { id: 'chatgpt', label: 'ChatGPT', icon: '🟢', color: '#10a37f', url: (p) => `https://chat.openai.com/?q=${encodeURIComponent(p)}` },
  { id: 'gemini',  label: 'Gemini',  icon: '🔵', color: '#1a73e8', url: (p) => `https://gemini.google.com/?q=${encodeURIComponent(p)}` },
];

function AiLabSegment({ segment, value, onChange, priorReflection = '', draft = {}, onDraftChange }) {
  const metadata = segment.domain_metadata || segment.scene_data?.domain_metadata || segment.metadata || {};
  const challengeBrief = metadata.challenge_brief || segment.script_text || '';
  const starterPromptTemplate = metadata.starter_prompt || '';
  const editableFields = metadata.editable_fields || [];
  const submissionPrompt = metadata.submission_prompt || 'Paste the AI\'s output and your annotation below.';
  const reflectionQuestions = metadata.reflection_questions || [];
  const toolRec = metadata.tool_recommendation || 'any';

  // Track learner's field edits — restore draft, else prefill first field from prior reflection
  const [fieldValues, setFieldValues] = React.useState(() => {
    if (draft?.fieldValues && typeof draft.fieldValues === 'object') {
      return { ...draft.fieldValues };
    }
    const init = {};
    editableFields.forEach((f, i) => {
      init[i] = (i === 0 && priorReflection) ? priorReflection : '';
    });
    return init;
  });
  const [selectedTool, setSelectedTool] = React.useState(
    draft?.selectedTool
      || (AI_TOOLS.find((t) => t.id === toolRec) ? toolRec : 'claude')
  );

  // Re-hydrate if parent restores draft after first paint
  React.useEffect(() => {
    if (draft?.fieldValues && typeof draft.fieldValues === 'object') {
      setFieldValues((prev) => {
        const same = JSON.stringify(prev) === JSON.stringify(draft.fieldValues);
        return same ? prev : { ...draft.fieldValues };
      });
    }
    if (draft?.selectedTool) setSelectedTool(draft.selectedTool);
  }, [draft?.fieldValues, draft?.selectedTool]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistDraft = (nextFields, nextTool) => {
    onDraftChange?.({
      kind: 'ai_lab',
      fieldValues: nextFields,
      selectedTool: nextTool,
      submission: value || '',
    });
  };
  const [promptCopied, setPromptCopied] = React.useState(false);
  const [showPrompt, setShowPrompt] = React.useState(false);

  // Build the final prompt by substituting filled-in field values
  const resolvedPrompt = React.useMemo(() => {
    let p = starterPromptTemplate;
    editableFields.forEach((f, i) => {
      const val = (fieldValues[i] || '').trim() || f.placeholder || f.key;
      // Replace the exact key string in the prompt template
      p = p.replace(f.key, val);
    });
    return p;
  }, [starterPromptTemplate, editableFields, fieldValues]);

  const tool = AI_TOOLS.find((t) => t.id === selectedTool) || AI_TOOLS[0];

  const handleOpenTool = () => {
    window.open(tool.url(resolvedPrompt), '_blank', 'noopener,noreferrer');
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(resolvedPrompt);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2000);
    } catch {
      // Fallback: select text from textarea
    }
  };

  const minWords = 30;
  const wordCount = (value || '').trim() ? value.trim().split(/\s+/).length : 0;
  const meetsMin = wordCount >= minWords;

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* Header badge */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
        padding: '10px 14px',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
        borderRadius: 10, color: '#e2e8f0',
      }}>
        <span style={{ fontSize: 24 }}>⚗️</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>AI Lab — Practice Challenge</div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            Take this prompt to ChatGPT, Claude, or Gemini. Work through it. Submit your result.
          </div>
        </div>
      </div>

      {/* Challenge brief */}
      {challengeBrief && (
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0',
          borderRadius: 10, padding: 18, marginBottom: 18,
          fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap',
          color: '#0f172a',
        }}>
          {/* Simple markdown-style rendering: strip ## headers to bold */}
          {challengeBrief.split('\n').map((line, i) => {
            if (line.startsWith('## ')) return <h3 key={i} style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700 }}>{line.slice(3)}</h3>;
            if (line.startsWith('**') && line.endsWith('**')) return <p key={i} style={{ fontWeight: 600, margin: '4px 0' }}>{line.slice(2, -2)}</p>;
            if (line.startsWith('- ')) return <li key={i} style={{ marginLeft: 16, marginBottom: 3 }}>{line.slice(2)}</li>;
            if (line === '') return <br key={i} />;
            return <span key={i}>{line}<br /></span>;
          })}
        </div>
      )}

      {/* Editable fields — learner fills these before the prompt is sent */}
      {editableFields.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5, color: '#475569', marginBottom: 10 }}>
            Fill in your details
          </div>
          {editableFields.map((f, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>
                {f.label}
              </label>
              <textarea
                value={fieldValues[i] || ''}
                onChange={(e) => {
                  const next = { ...fieldValues, [i]: e.target.value };
                  setFieldValues(next);
                  persistDraft(next, selectedTool);
                }}
                placeholder={f.placeholder || ''}
                rows={3}
                style={{
                  width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1',
                  borderRadius: 7, fontSize: 13, fontFamily: 'inherit',
                  lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box',
                  background: '#fff', color: '#0f172a',
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Prompt preview (collapsible) */}
      <div style={{ marginBottom: 18 }}>
        <button
          onClick={() => setShowPrompt((v) => !v)}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: 13, fontWeight: 600, color: '#3b82f6', display: 'flex',
            alignItems: 'center', gap: 5, marginBottom: 8,
          }}
        >
          {showPrompt ? '▾' : '▸'} {showPrompt ? 'Hide' : 'Preview'} your prompt
        </button>
        {showPrompt && (
          <div style={{ position: 'relative' }}>
            <pre style={{
              background: '#0f172a', color: '#e2e8f0', borderRadius: 8,
              padding: '14px 16px', fontSize: 12.5, lineHeight: 1.65,
              overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              maxHeight: 320, overflowY: 'auto', margin: 0,
            }}>
              {resolvedPrompt}
            </pre>
            <button
              onClick={handleCopyPrompt}
              style={{
                position: 'absolute', top: 8, right: 8,
                background: promptCopied ? '#10b981' : '#1e293b',
                color: '#fff', border: 'none', borderRadius: 5,
                padding: '3px 9px', fontSize: 11, cursor: 'pointer', fontWeight: 600,
              }}
            >
              {promptCopied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        )}
      </div>

      {/* Tool selector + launch */}
      <div style={{
        background: '#f1f5f9', borderRadius: 10, padding: 16, marginBottom: 18,
      }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>
          Open in your AI tool
          {toolRec !== 'any' && (
            <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 6 }}>
              (recommended: {toolRec.charAt(0).toUpperCase() + toolRec.slice(1)})
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {AI_TOOLS.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setSelectedTool(t.id);
                persistDraft(fieldValues, t.id);
              }}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.12s',
                border: selectedTool === t.id ? `2px solid ${t.color}` : '2px solid transparent',
                background: selectedTool === t.id ? '#fff' : '#e2e8f0',
                color: selectedTool === t.id ? t.color : '#64748b',
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
        <button
          onClick={handleOpenTool}
          style={{
            width: '100%', padding: '10px 16px',
            background: tool.color, color: '#fff', border: 'none',
            borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {tool.icon} Open {tool.label} with my prompt →
        </button>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, textAlign: 'center' }}>
          Opens in a new tab using your existing {tool.label} account.
          Work through the challenge there, then come back here to submit.
        </div>
      </div>

      {/* Submission area */}
      <div style={{ marginBottom: 16 }}>
        <div style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderRadius: 8, padding: 14, marginBottom: 10, color: '#78350f',
        }}>
          <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
            Submit your result
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
            {submissionPrompt}
          </div>
        </div>
        <textarea
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            onDraftChange?.({
              kind: 'ai_lab',
              fieldValues,
              selectedTool,
              submission: e.target.value,
            });
          }}
          placeholder="Paste the AI's output here, then add your annotation…"
          rows={10}
          style={{
            width: '100%', padding: 12, border: '1px solid #d1d5db',
            borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
            lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box',
            background: '#fff', color: '#0f172a',
          }}
        />
        <div style={{ fontSize: 12, color: meetsMin ? '#10b981' : '#64748b', marginTop: 5 }}>
          {wordCount} words {meetsMin ? '✓' : `(aim for at least ${minWords})`}
        </div>
      </div>

      {/* Reflection questions */}
      {reflectionQuestions.length > 0 && (
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0',
          borderRadius: 8, padding: 14,
        }}>
          <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#475569', marginBottom: 10 }}>
            Reflect on your session
          </div>
          <ol style={{ paddingLeft: 20, margin: 0 }}>
            {reflectionQuestions.map((q, i) => (
              <li key={i} style={{ fontSize: 13.5, lineHeight: 1.6, color: '#334155', marginBottom: 8 }}>
                {q}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}


function QuestionSegment({ segment, answers, onAnswer, showAnswersImmediately, reviewMode = false }) {
  const questions = segment.questions || [];

  if (questions.length === 0) {
    return <div style={mutedStyle}>This segment has no questions.</div>;
  }

  const answeredCount = Object.keys(answers).length;
  const correctCount = questions.filter((q) => {
    const choiceId = answers[q.question_id];
    if (!choiceId) return false;
    const choice = (q.choices || []).find((c) => c.choice_id === choiceId);
    return !!(choice && (choice.correct || choice.is_correct));
  }).length;

  const allAnswered = answeredCount === questions.length && questions.length > 0;
  const pct = questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0;
  const passed = pct >= 70;

  return (
    <div>
      <div style={{
        background: reviewMode ? '#f0fdf4' : '#eff6ff',
        border: `1px solid ${reviewMode ? '#bbf7d0' : '#bfdbfe'}`,
        borderRadius: 8,
        padding: '10px 14px', marginBottom: 12, fontSize: 13,
        color: reviewMode ? '#166534' : '#1e40af',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 16 }}>{reviewMode ? '👁' : '🎯'}</span>
        <span>
          {reviewMode
            ? 'Review mode — showing your saved answers. Navigation is unlocked.'
            : <>Passing score: <strong>70%</strong> — click an answer to submit (your choice is final)</>}
        </span>
      </div>
      <div style={quizHeaderStyle}>
        <span>{answeredCount}/{questions.length} answered</span>
        {answeredCount > 0 && <span>· {correctCount} correct</span>}
      </div>
      {questions.map((q, i) => (
        <QuestionCard
          key={q.question_id || i}
          index={i + 1}
          question={q}
          selected={answers[q.question_id]}
          onSelect={(choiceId) => onAnswer(q.question_id, choiceId)}
          showAnswersImmediately={showAnswersImmediately || reviewMode}
          locked={reviewMode && !!answers[q.question_id]}
        />
      ))}
      {allAnswered && (
        <div style={{
          background: passed ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${passed ? '#a7f3d0' : '#fecaca'}`,
          borderRadius: 10, padding: 18, marginTop: 16, textAlign: 'center',
        }}>
          <div style={{ fontSize: 32, marginBottom: 6 }}>{passed ? '🎉' : '📚'}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: passed ? '#065f46' : '#991b1b', marginBottom: 4 }}>
            You scored {correctCount}/{questions.length} ({pct}%)
          </div>
          <div style={{ fontSize: 14, color: passed ? '#047857' : '#b91c1c' }}>
            {reviewMode
              ? (passed ? 'Previously passed — browse freely.' : 'Previous attempt below the pass mark.')
              : (passed ? 'Great work — you passed!' : 'You need 70% to pass. Review the explanations and try again next time.')}
          </div>
        </div>
      )}
      {reviewMode && !allAnswered && (
        <div style={{
          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10,
          padding: 14, marginTop: 16, fontSize: 13, color: '#92400e',
        }}>
          No saved answers for this quiz yet (completed before answer persistence).
          You can still browse the module with Next / Previous.
        </div>
      )}
    </div>
  );
}


/**
 * Deterministic shuffle seeded on question_id — same learner sees the same
 * shuffled order each visit, but the correct answer is NOT always position 0.
 * Uses a simple LCG so no external library is required.
 */
function seededShuffle(arr, seed) {
  const out = [...arr];
  let s = seed;
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    const j = Math.abs(s) % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
function strSeed(str) {
  let h = 0;
  for (let i = 0; i < (str || '').length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  return Math.abs(h) || 1;
}

function QuestionCard({ index, question, selected, onSelect, showAnswersImmediately, locked: lockedProp = false }) {
  const [attempts, setAttempts] = React.useState(0);
  const [locked, setLocked] = React.useState(!!lockedProp || !!selected);
  const [hintsRevealed, setHintsRevealed] = React.useState(0);
  const [selfExplCollapsed, setSelfExplCollapsed] = React.useState(true);
  const maxAttempts = question.max_attempts || 1;
  const hints = question.hints || [];

  // When Review hydrates a prior answer, lock the card and show feedback.
  React.useEffect(() => {
    if (lockedProp || selected) setLocked(true);
  }, [lockedProp, selected]);

  // Shuffle choices once per question so the correct answer is never always first.
  // Using useMemo ensures the order is stable across re-renders within the same session.
  const shuffledChoices = React.useMemo(
    () => seededShuffle(question.choices || [], strSeed(question.question_id || question.prompt || question.stem || String(index))),
    [question.question_id, question.prompt, question.stem, index], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const isAnswered = !!selected;
  const selectedChoice = shuffledChoices.find((c) => c.choice_id === selected);
  const isCorrect = !!(selectedChoice && (selectedChoice.correct || selectedChoice.is_correct));
  const exhausted = !isCorrect && attempts >= maxAttempts;
  const showFeedback = isAnswered && (showAnswersImmediately || isCorrect || exhausted || locked || lockedProp);
  const correctChoice = shuffledChoices.find((c) => c.correct || c.is_correct);

  const handleSelect = (choiceId) => {
    if (locked || lockedProp) return;
    onSelect(choiceId);
    const choice = shuffledChoices.find((c) => c.choice_id === choiceId);
    const correct = !!(choice && (choice.correct || choice.is_correct));
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    if (correct || newAttempts >= maxAttempts) {
      setLocked(true);
    }
  };

  const handleRetry = () => {
    if (locked || isCorrect) return;
    onSelect(null);
  };

  // Build the display text from available fields.
  // 'prompt' is the canonical field (AI/tutorial format).
  // LSAT-style questions use 'stimulus' (argument/passage) + 'stem' (question).
  // 'passage_text' carries RC passage text hydrated by the session API.
  const stimText = question.stimulus || question.passage_text || '';
  const stemText = question.stem || '';
  const promptText = question.prompt
    || (stimText && stemText
        ? `${stimText}\n\n---\n\n${stemText}`
        : stemText || stimText || '');

  // Split passage from question stem when separated by "---"
  const separatorIdx = promptText.indexOf('\n\n---\n\n');
  const hasPassage = separatorIdx > -1;
  const passageText = hasPassage ? promptText.slice(0, separatorIdx) : null;
  const questionStem = hasPassage ? promptText.slice(separatorIdx + 7) : promptText;

  return (
    <div style={questionCardStyle}>
      {hasPassage && (
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
          padding: '14px 16px', marginBottom: 14, maxHeight: 300, overflowY: 'auto',
          fontSize: 14, lineHeight: 1.7, color: '#334155', whiteSpace: 'pre-wrap',
        }}>
          <div style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b', marginBottom: 8 }}>
            Reading Passage
          </div>
          {passageText}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 15, color: '#111827', lineHeight: 1.5 }}>
          {index}. {questionStem}
        </div>
        {maxAttempts > 1 && attempts > 0 && !isCorrect && !exhausted && (
          <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap', marginLeft: 8 }}>
            Attempt {attempts}/{maxAttempts}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {shuffledChoices.map((c, ci) => {
          const isSelected = selected === c.choice_id;
          const isChoiceCorrect = c.correct || c.is_correct;
          let bg = '#fff';
          let border = '#d1d5db';
          if (showFeedback) {
            if (isChoiceCorrect) { bg = '#ecfdf5'; border = '#10b981'; }
            else if (isSelected) { bg = '#fef2f2'; border = '#ef4444'; }
          } else if (isAnswered && isSelected && !isCorrect) {
            bg = '#fef2f2'; border = '#ef4444';
          } else if (isSelected) {
            bg = '#eff6ff'; border = '#3b82f6';
          }
          const choiceLabel = String.fromCharCode(65 + ci); // A, B, C, D…
          return (
            <button
              key={c.choice_id}
              onClick={() => !locked && handleSelect(c.choice_id)}
              disabled={locked}
              style={{
                textAlign: 'left',
                padding: '10px 12px',
                background: bg,
                border: `1px solid ${border}`,
                borderRadius: 6,
                cursor: locked ? 'default' : 'pointer',
                fontSize: 14,
                color: '#111827',
              }}
            >
              <strong style={{ marginRight: 8 }}>{choiceLabel}.</strong>
              {c.text}
              {showFeedback && isChoiceCorrect && <span style={{ color: '#059669', marginLeft: 8 }}>✓</span>}
              {showFeedback && isSelected && !isChoiceCorrect && <span style={{ color: '#dc2626', marginLeft: 8 }}>✗</span>}
            </button>
          );
        })}
      </div>

      {/* Progressive hint disclosure — available before final answer */}
      {hints.length > 0 && !showFeedback && (
        <div style={{ marginTop: 10 }}>
          {hintsRevealed > 0 && (
            <div style={{ marginBottom: 8 }}>
              {hints.slice(0, hintsRevealed).map((h, i) => (
                <div key={i} style={{
                  padding: '8px 12px', background: '#fffbeb', border: '1px solid #fcd34d',
                  borderRadius: 6, fontSize: 13, color: '#92400e', marginBottom: 4,
                  display: 'flex', gap: 6, lineHeight: 1.5,
                }}>
                  <span style={{ fontWeight: 700, flexShrink: 0 }}>Hint {i + 1}:</span>
                  <span>{h}</span>
                </div>
              ))}
            </div>
          )}
          {hintsRevealed < hints.length && (
            <button
              onClick={() => setHintsRevealed((n) => n + 1)}
              style={{
                background: 'none', border: '1px dashed #d97706', borderRadius: 6,
                padding: '5px 14px', fontSize: 12, fontWeight: 600, color: '#d97706',
                cursor: 'pointer',
              }}
            >
              {hintsRevealed === 0 ? 'Need a hint?' : `Show next hint (${hints.length - hintsRevealed} left)`}
            </button>
          )}
        </div>
      )}

      {isAnswered && !isCorrect && !locked && maxAttempts > 1 && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#dc2626' }}>Not quite.</span>
          <button
            onClick={handleRetry}
            style={{
              background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6,
              padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Try Again ({maxAttempts - attempts} left)
          </button>
        </div>
      )}

      {showFeedback && (
        <div style={{
          marginTop: 12,
          padding: 14,
          background: isCorrect ? '#ecfdf5' : '#fef2f2',
          borderLeft: `4px solid ${isCorrect ? '#10b981' : '#ef4444'}`,
          borderRadius: 6,
          fontSize: 13,
          color: '#374151',
          lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: isCorrect ? '#065f46' : '#991b1b' }}>
            {isCorrect ? 'Correct!' : `The correct answer is: ${correctChoice?.text || 'N/A'}`}
          </div>
          {question.explanation && (
            <div style={{ marginBottom: 10 }}>{question.explanation}</div>
          )}
          {/* Per-choice breakdown — always shown after answering */}
          <div style={{ marginTop: 8, borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#6b7280', marginBottom: 6 }}>
              Answer breakdown:
            </div>
            {shuffledChoices.map((c, ci) => {
              const wasSelected = selected === c.choice_id;
              const isChoiceCorrect = c.correct || c.is_correct;
              const choiceLabel = String.fromCharCode(65 + ci);
              return (
                <div key={c.choice_id} style={{
                  display: 'flex', gap: 6, marginBottom: 5, fontSize: 13, lineHeight: 1.5,
                  padding: '4px 8px', borderRadius: 4,
                  background: isChoiceCorrect ? 'rgba(16,185,129,0.06)' : (wasSelected ? 'rgba(239,68,68,0.06)' : 'transparent'),
                  color: isChoiceCorrect ? '#065f46' : '#7f1d1d',
                }}>
                  <span style={{ fontWeight: 700, minWidth: 22, flexShrink: 0 }}>
                    {isChoiceCorrect ? '✓' : '✗'} {choiceLabel}.
                  </span>
                  <span>
                    <strong style={{ fontWeight: 600 }}>{c.text}</strong>
                    {c.explanation ? ` — ${c.explanation}` : ''}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Self-explanation prompt — encourages deeper processing on wrong answers */}
          {!isCorrect && (
            <div style={{ marginTop: 10, borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: 10 }}>
              <button
                onClick={() => setSelfExplCollapsed((v) => !v)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  fontSize: 13, fontWeight: 600, color: '#4f46e5', display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                {selfExplCollapsed ? '▶' : '▼'} Why was my answer wrong?
              </button>
              {!selfExplCollapsed && (
                <div style={{
                  marginTop: 8, padding: 12, background: '#eef2ff', borderRadius: 6,
                  fontSize: 13, lineHeight: 1.6, color: '#3730a3',
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Reflect on your reasoning:</div>
                  {selectedChoice?.explanation ? (
                    <div>
                      <div style={{ marginBottom: 6 }}><strong>Your answer ({(selected || '').toUpperCase()}):</strong> {selectedChoice.text}</div>
                      <div style={{ marginBottom: 6 }}>{selectedChoice.explanation}</div>
                      {selectedChoice.distractor_type && (
                        <div style={{ fontSize: 11, color: '#6366f1', fontStyle: 'italic' }}>
                          This is a common trap: {selectedChoice.distractor_type.replace(/_/g, ' ')}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      Compare your choice with the correct answer above. What assumption did you make that led you astray? Understanding the difference helps solidify the concept.
                    </div>
                  )}
                  {question.reasoning_prompt && (
                    <div style={{ marginTop: 8, padding: '8px 10px', background: '#ddd6fe', borderRadius: 4, fontSize: 12, color: '#4c1d95' }}>
                      <strong>Key insight:</strong> {question.reasoning_prompt}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Canvas Drawer — slide-out panel showing saved canvases.
// Lets learners access, review, and copy from their saved canvases
// without leaving the tutorial player.
// ─────────────────────────────────────────────────────────────────────
function CanvasDrawer({ domain, onClose }) {
  const [canvases, setCanvases] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [expandedIdx, setExpandedIdx] = React.useState(null);

  React.useEffect(() => {
    const token = localStorage.getItem('scenario_os_token');
    fetch(`/scenario-os/curriculum/resources?resource_type=prompt_canvas&domain=${encodeURIComponent(domain)}`, {
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((data) => setCanvases(data.resources || []))
      .catch((e) => setError(e.message || 'Failed to load canvases'))
      .finally(() => setLoading(false));
  }, [domain]);

  return (
    <>
      <div onClick={onClose} style={drawerOverlayStyle} />
      <div style={drawerPanelStyle}>
        <div style={drawerHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>📋</span>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>My Canvas Library</h3>
          </div>
          <button onClick={onClose} style={drawerCloseBtnStyle}>✕</button>
        </div>

        <div style={drawerBodyStyle}>
          {loading && <div style={{ color: '#6b7280', fontSize: 14, padding: 20 }}>Loading canvases...</div>}
          {error && <div style={{ color: '#dc2626', fontSize: 14, padding: 20 }}>{error}</div>}
          {!loading && !error && canvases.length === 0 && (
            <div style={{ color: '#6b7280', fontSize: 14, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>📋</div>
              No saved canvases yet. Complete a Prompt Canvas segment and save it to see it here.
            </div>
          )}
          {canvases.map((c, i) => (
            <div key={c.resource_url || i} style={drawerCardStyle}>
              <button
                onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                style={drawerCardHeaderBtnStyle}
              >
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{c.title || 'Untitled Canvas'}</div>
                  {c.difficulty && (
                    <span style={{ fontSize: 11, color: '#64748b' }}>
                      {c.difficulty.charAt(0).toUpperCase() + c.difficulty.slice(1)}
                    </span>
                  )}
                </div>
                <span style={{ color: '#94a3b8', fontSize: 12 }}>{expandedIdx === i ? '▾' : '▸'}</span>
              </button>
              {expandedIdx === i && (
                <div style={drawerCardBodyStyle}>
                  {(c.steps || []).map((step, si) => (
                    <div key={si} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 3 }}>
                        {si + 1}. {step.label || `Step ${si + 1}`}
                      </div>
                      <pre style={{
                        background: '#f1f5f9', borderRadius: 6, padding: '8px 10px',
                        fontSize: 12, lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word', color: '#0f172a',
                      }}>
                        <LinkifiedText text={step.content || ''} />
                      </pre>
                    </div>
                  ))}
                  {c.key_insight && (
                    <div style={{
                      background: '#fef3c7', borderRadius: 6, padding: 10,
                      fontSize: 12, color: '#92400e', marginTop: 8,
                    }}>
                      <strong>Key Insight:</strong> {c.key_insight}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

const drawerOverlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)',
  zIndex: 9000, cursor: 'pointer',
};
const drawerPanelStyle = {
  position: 'fixed', top: 0, right: 0, bottom: 0, width: 400, maxWidth: '90vw',
  background: '#fff', zIndex: 9001, boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
  display: 'flex', flexDirection: 'column',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};
const drawerHeaderStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '16px 20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
};
const drawerCloseBtnStyle = {
  background: 'none', border: 'none', fontSize: 18, color: '#6b7280',
  cursor: 'pointer', padding: 4,
};
const drawerBodyStyle = { flex: 1, overflowY: 'auto', padding: '12px 16px' };
const drawerCardStyle = {
  border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 8, overflow: 'hidden',
};
const drawerCardHeaderBtnStyle = {
  width: '100%', display: 'flex', alignItems: 'center', gap: 8,
  padding: '10px 12px', background: '#fafbfc', border: 'none',
  cursor: 'pointer', fontSize: 'inherit',
};
const drawerCardBodyStyle = {
  padding: '10px 12px', borderTop: '1px solid #f1f5f9', background: '#fff',
};


// ─────────────────────────────────────────────────────────────────────
// RubricSubmissionWidget — shown on reflection/ai_lab/prompt_canvas
// segments when the scene has a rubric defined. Lets learners submit
// their work for review and view feedback after grading.
// ─────────────────────────────────────────────────────────────────────
function RubricSubmissionWidget({ sceneId, submissionText }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [error, setError] = useState('');
  const [existingSub, setExistingSub] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('scenario_os_token');
    if (!token || !sceneId) return;
    fetch(`/scenario-os/coach/rubrics/submissions?scene_id=${encodeURIComponent(sceneId)}&limit=1`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const subs = data?.submissions || [];
        if (subs.length > 0) {
          setExistingSub(subs[0]);
          setSubmissionId(subs[0].submission_id);
          setSubmitted(true);
        }
      })
      .catch(() => {});
  }, [sceneId]);

  const handleSubmit = async () => {
    if (!submissionText?.trim()) { setError('Write your response before submitting.'); return; }
    setSubmitting(true);
    setError('');
    const token = localStorage.getItem('scenario_os_token');
    try {
      const res = await fetch(`/scenario-os/coach/rubrics/submissions/${encodeURIComponent(sceneId)}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ text: submissionText }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSubmissionId(data.submission_id);
      setSubmitted(true);
    } catch (e) {
      setError(e.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (showFeedback && submissionId) {
    return (
      <div style={{ marginTop: 16 }}>
        <SubmissionFeedback submissionId={submissionId} onClose={() => setShowFeedback(false)} />
      </div>
    );
  }

  const status = existingSub?.status;
  const hasGrade = status === 'reviewed' || status === 'ai_graded';

  return (
    <div style={{
      marginTop: 16, padding: 16,
      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
      border: '1px solid #93c5fd', borderRadius: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 18 }}>📝</span>
        <span style={{ fontWeight: 700, fontSize: 14, color: '#1e40af' }}>
          {submitted ? 'Submitted for Review' : 'Submit for Review'}
        </span>
        {status && (
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
            background: hasGrade ? '#dcfce7' : '#fef3c7',
            color: hasGrade ? '#166534' : '#92400e',
            marginLeft: 4,
          }}>
            {status === 'reviewed' ? 'Graded' : status === 'ai_graded' ? 'AI Graded' : 'Pending'}
          </span>
        )}
      </div>

      {!submitted && (
        <>
          <div style={{ fontSize: 13, color: '#374151', marginBottom: 10, lineHeight: 1.5 }}>
            Your response will be evaluated against the rubric criteria. You will receive
            detailed feedback on each criterion.
          </div>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6,
              padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {submitting ? 'Submitting…' : 'Submit for Review'}
          </button>
        </>
      )}

      {submitted && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#059669' }}>
            {hasGrade ? 'Your submission has been graded.' : 'Your submission is being reviewed.'}
          </span>
          {hasGrade && (
            <button
              onClick={() => setShowFeedback(true)}
              style={{
                background: '#059669', color: '#fff', border: 'none', borderRadius: 6,
                padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              View Feedback
            </button>
          )}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626' }}>{error}</div>
      )}
    </div>
  );
}


function ProgressBar({ current, total }) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ background: '#e5e7eb', borderRadius: 999, height: 6, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, background: '#3b82f6', height: '100%' }} />
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Inline styles (kept here so the page is self-contained)
// ──────────────────────────────────────────────────────────────────────────

const pageStyle = {
  maxWidth: 920, margin: '24px auto', padding: '0 24px', fontFamily: 'system-ui, -apple-system, sans-serif',
  color: '#1f2937',
};
const headerStyle = {
  display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20,
  paddingBottom: 16, borderBottom: '1px solid #e5e7eb',
};
const backBtnStyle = {
  background: 'transparent', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 14, padding: 4,
};
const mutedStyle = { color: '#6b7280', fontSize: 14, padding: '24px 0' };
const errorStyle = {
  color: '#b3261e', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
  padding: 12, borderRadius: 6, fontSize: 14,
};
const cardGridStyle = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12,
};
const cardStyle = {
  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16,
  textAlign: 'left', cursor: 'pointer', transition: 'border-color .15s, box-shadow .15s',
};
const metaRowStyle = {
  display: 'flex', gap: 12, fontSize: 12, color: '#6b7280', alignItems: 'center', flexWrap: 'wrap',
};
const segmentCardStyle = {
  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 16,
  color: '#111827',
};
const segmentHeaderStyle = { marginBottom: 4 };
const segmentBadgeStyle = {
  background: '#dbeafe', color: '#1e40af', fontSize: 11, fontWeight: 700,
  padding: '2px 8px', borderRadius: 999, letterSpacing: 0.5,
};
const navBarStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 0', borderTop: '1px solid #e5e7eb', marginTop: 8,
};
const navBtnStyle = (primary) => ({
  background: primary ? '#3b82f6' : '#fff',
  color: primary ? '#fff' : '#374151',
  border: `1px solid ${primary ? '#3b82f6' : '#d1d5db'}`,
  borderRadius: 6, padding: '8px 16px', fontSize: 14, cursor: 'pointer',
});
const quizHeaderStyle = {
  display: 'flex', gap: 12, fontSize: 13, color: '#6b7280', marginBottom: 16,
};
const questionCardStyle = {
  background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8,
  padding: 16, marginBottom: 12, color: '#1a1a2e',
};

function difficultyBadge(diff) {
  const colors = {
    beginner: { bg: '#dcfce7', fg: '#166534' },
    intermediate: { bg: '#fef3c7', fg: '#92400e' },
    advanced: { bg: '#fee2e2', fg: '#991b1b' },
  };
  const c = colors[diff] || colors.beginner;
  return {
    background: c.bg, color: c.fg, fontSize: 11, fontWeight: 600,
    padding: '2px 8px', borderRadius: 999,
  };
}
